import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { Canvas, useThree } from '@react-three/fiber';
import { Environment, OrbitControls, Grid, ContactShadows } from '@react-three/drei';
import * as THREE from 'three';
import URDFLoader from 'urdf-loader';
import { LoadingManager } from 'three';
import {
  ArrowLeft, Save, Play, Pause, ZoomIn, ZoomOut,
  RotateCcw, Layers, ChevronDown, ChevronRight, SlidersHorizontal, RefreshCw, Check,
  Target, Bookmark, Crosshair, Activity, AlertTriangle, Footprints, Wand2, LineChart, Film
} from 'lucide-react';
import api, { type SmartFootAdjustRequest, type FootLevelingRequest, type ZmpRiskCheckRequest, type ZmpRiskCheckResponse, type WaistAdjustRequest, type TaskDetailResponse } from '../api';
import { useStore } from '../store/useStore';
import type { JointKey, JointAngles, KeyframeInterval, KeyframeData } from '../types';
import { JOINT_DEFS, emptyJoints, INTERVAL_COLORS } from '../types';
import type { RootTransform, SimulationJsonResponse } from '../utils/simulationCsv';
import Modal from '../components/Modal';
import { toast, type ToastType } from '../components/Toast';
import { buildSimulationPayloadFromFrames, loadSimulationCsv, loadLocalSimulationCsv } from '../utils/simulationCsv';
import { RobotTrajectoryEditor } from '../utils/robotEdit';
import { DEFAULT_ROBOT_MODEL_ID, getRobotModelConfig, ROBOT_MODELS, findModelConfigByRobotModel, type RobotModelConfig } from '../config/robotModels1';
import tableHeadings from '../config/table_heading';
import MujocoViewer from '../components/MujocoViewer';
import ErrorBoundary from '../components/ErrorBoundary';
import { Muxer, ArrayBufferTarget } from 'mp4-muxer';

// ===== 本地轨迹计算（替代后端计算接口） =====
// 当 MuJoCo 就绪时用前端 RobotTrajectoryEditor 本地计算；否则回退到后端接口。
// 本地计算抛错时也会回退，保证功能始终可用。

type MjHandleLike = { model: any; data: any; M: any; qmap?: Record<string, number> };

function createTrajectoryEditor(mj: MjHandleLike | null): RobotTrajectoryEditor | null {
  if (!mj || !mj.model || !mj.data || !mj.M) return null;
  try {
    return new RobotTrajectoryEditor(mj);
  } catch (error) {
    console.error('[robotEdit] 创建本地编辑器失败:', error);
    return null;
  }
}

function runLocalTrajectoryOp(
  mj: MjHandleLike | null,
  payload: SimulationJsonResponse,
  op: (editor: RobotTrajectoryEditor) => void,
): SimulationJsonResponse | null {
  const editor = createTrajectoryEditor(mj);
  if (!editor) return null;
  editor.loadFromArray(payload);
  op(editor);
  return editor.dumpToArray() as unknown as SimulationJsonResponse;
}

// ===== Base Control 面板（参考 Python base_control_widget.py） =====
// 四元数 → 欧拉角 (ZYX 顺序，和 MuJoCo 一致)
function quatToEulerXYZ(q: [number, number, number, number]): [number, number, number] {
  const [x, y, z, w] = q;
  // Roll (X)
  const sinr_cosp = 2 * (w * x + y * z);
  const cosr_cosp = 1 - 2 * (x * x + y * y);
  const roll = Math.atan2(sinr_cosp, cosr_cosp);
  // Pitch (Y)
  const sinp = 2 * (w * y - z * x);
  const pitch = Math.abs(sinp) >= 1 ? Math.sign(sinp) * Math.PI / 2 : Math.asin(sinp);
  // Yaw (Z)
  const siny_cosp = 2 * (w * z + x * y);
  const cosy_cosp = 1 - 2 * (y * y + z * z);
  const yaw = Math.atan2(siny_cosp, cosy_cosp);
  return [roll, pitch, yaw];
}
// 欧拉角 → 四元数 (ZYX 顺序)
function eulerXYZToQuat(euler: [number, number, number]): [number, number, number, number] {
  const [roll, pitch, yaw] = euler;
  const cr = Math.cos(roll * 0.5), sr = Math.sin(roll * 0.5);
  const cp = Math.cos(pitch * 0.5), sp = Math.sin(pitch * 0.5);
  const cy = Math.cos(yaw * 0.5), sy = Math.sin(yaw * 0.5);
  const w = cr * cp * cy + sr * sp * sy;
  const x = sr * cp * cy - cr * sp * sy;
  const y = cr * sp * cy + sr * cp * sy;
  const z = cr * cp * sy - sr * sp * cy;
  return [x, y, z, w];
}

/** 关节曲线面板 — 用 Canvas API 绘制关节值随帧变化的曲线，参照 viewer/core_playback.py */
function JointCurvePanel({
  frames, currentFrame, jointKey, onJointKeyChange, jointDefs,
}: {
  frames: JointAngles[];
  currentFrame: number;
  jointKey: JointKey | '';
  onJointKeyChange: (key: JointKey | '') => void;
  jointDefs: { key: JointKey; label: string; labelEn: string; min: number; max: number }[];
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState({ w: 400, h: 220 });

  // 响应容器大小
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;
      if (width > 0) setCanvasSize({ w: Math.floor(width), h: 220 });
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // 绘制曲线
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || frames.length === 0 || !jointKey) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const W = canvasSize.w;
    const H = canvasSize.h;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;
    ctx.scale(dpr, dpr);

    const pad = { top: 24, right: 16, bottom: 28, left: 50 };
    const plotW = W - pad.left - pad.right;
    const plotH = H - pad.top - pad.bottom;

    const def = jointDefs.find(j => j.key === jointKey);
    if (!def) return;

    // 提取该关节所有帧的值
    const values = frames.map(f => f[jointKey] ?? 0);
    const yMin = def.min;
    const yMax = def.max;
    const yRange = yMax - yMin || 1;
    const xMax = values.length - 1;

    // 坐标转换
    const toX = (i: number) => pad.left + (i / Math.max(xMax, 1)) * plotW;
    const toY = (v: number) => pad.top + (1 - (v - yMin) / yRange) * plotH;

    // 清空
    ctx.clearRect(0, 0, W, H);

    // 背景
    ctx.fillStyle = '#FAFBFC';
    ctx.fillRect(pad.left, pad.top, plotW, plotH);

    // 网格线
    ctx.strokeStyle = '#E5E7EB';
    ctx.lineWidth = 0.5;
    const yTicks = 5;
    for (let i = 0; i <= yTicks; i++) {
      const v = yMin + (yRange * i) / yTicks;
      const y = toY(v);
      ctx.beginPath(); ctx.moveTo(pad.left, y); ctx.lineTo(pad.left + plotW, y); ctx.stroke();
      // Y轴标签
      ctx.fillStyle = '#9CA3AF';
      ctx.font = '9px monospace';
      ctx.textAlign = 'right';
      ctx.fillText(v.toFixed(2), pad.left - 4, y + 3);
    }
    // X轴标签
    const xTicks = Math.min(10, values.length);
    for (let i = 0; i <= xTicks; i++) {
      const frameIdx = Math.round((i / xTicks) * xMax);
      const x = toX(frameIdx);
      ctx.beginPath(); ctx.moveTo(x, pad.top); ctx.lineTo(x, pad.top + plotH); ctx.stroke();
      ctx.fillStyle = '#9CA3AF';
      ctx.font = '9px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(String(frameIdx + 1), x, pad.top + plotH + 14);
    }

    // 关节上下限虚线
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = '#EF4444';
    ctx.lineWidth = 1;
    const yHigh = toY(yMax);
    const yLow = toY(yMin);
    ctx.beginPath(); ctx.moveTo(pad.left, yHigh); ctx.lineTo(pad.left + plotW, yHigh); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(pad.left, yLow); ctx.lineTo(pad.left + plotW, yLow); ctx.stroke();
    ctx.setLineDash([]);

    // 主曲线
    ctx.strokeStyle = '#3B82F6';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    for (let i = 0; i < values.length; i++) {
      const x = toX(i);
      const y = toY(values[i]);
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.stroke();

    // 当前帧竖线
    if (currentFrame >= 0 && currentFrame < values.length) {
      const cx = toX(currentFrame);
      ctx.strokeStyle = '#22C55E';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([3, 3]);
      ctx.beginPath(); ctx.moveTo(cx, pad.top); ctx.lineTo(cx, pad.top + plotH); ctx.stroke();
      ctx.setLineDash([]);

      // 红点
      const cy = toY(values[currentFrame]);
      ctx.fillStyle = '#EF4444';
      ctx.beginPath();
      ctx.arc(cx, cy, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }

    // 标题
    ctx.fillStyle = '#1E293B';
    ctx.font = 'bold 11px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(`${def.label} 轨迹曲线`, pad.left, 14);

    // 帧号
    ctx.fillStyle = '#64748B';
    ctx.font = '9px sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(`帧数`, pad.left + plotW, H - 4);
  }, [frames, currentFrame, jointKey, jointDefs, canvasSize]);

  if (frames.length === 0) return null;

  return (
    <div className="px-4 pb-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-medium text-slate-600">选择关节:</span>
        <select
          value={jointKey}
          onChange={e => onJointKeyChange(e.target.value as JointKey | '')}
          className="flex-1 text-xs border border-slate-200 rounded-md px-2 py-1 bg-white focus:border-brand outline-none"
        >
          <option value="">-- 请选择 --</option>
          {jointDefs.map(j => (
            <option key={j.key} value={j.key}>{j.label}</option>
          ))}
        </select>
        {jointKey && (
          <span className="text-[10px] text-slate-400 font-mono">
            帧 {currentFrame + 1}/{frames.length}
          </span>
        )}
      </div>
      <div ref={containerRef} className="w-full bg-white border border-slate-200 rounded-lg overflow-hidden">
        <canvas ref={canvasRef} style={{ width: canvasSize.w, height: canvasSize.h, display: 'block' }} />
      </div>
    </div>
  );
}

function BaseControlPanel({
  mj, currentFrame, rootTransform, onChanged, onBlurCommit,
  onResetPos, onResetRot, onResetAll, syncTick,
}: {
  mj: { model: any; data: any; M: any } | null;
  currentFrame: number;
  rootTransform: RootTransform | null;
  onChanged: (nextRt: RootTransform) => void;
  onBlurCommit?: () => void;
  onResetPos: () => void;
  onResetRot: () => void;
  onResetAll: () => void;
  syncTick: number;
}) {
  // Hooks must run before any early return to avoid React hooks order error
  const addr = useMemo(() => {
    const model = mj?.model;
    if (!model) return 0;
    for (let i = 0; i < model.njnt; i++) {
      if (model.jnt_type[i] === 0) return model.jnt_qposadr[i];
    }
    return 0;
  }, [mj?.model]);

  const pos = rootTransform?.pos ?? [0, 0, 0];
  const quat = rootTransform?.quat ?? [0, 0, 0, 1];
  const euler = useMemo(() => quatToEulerXYZ(quat as [number, number, number, number]), [quat]);

  // 本地字符串 state 管理输入框显示值，输入过程中不提交，失焦时才提交
  const [posStrs, setPosStrs] = useState(['', '', '']);
  const [eulerStrs, setEulerStrs] = useState(['', '', '']);
  const [quatStrs, setQuatStrs] = useState(['', '', '', '']);

  // 当 rootTransform 变化时同步显示值（仅非聚焦状态，避免打断用户输入）
  const posFocusedRef = useRef([false, false, false]);
  const eulerFocusedRef = useRef([false, false, false]);
  const quatFocusedRef = useRef([false, false, false, false]);

  useEffect(() => {
    setPosStrs(prev => prev.map((s, i) => posFocusedRef.current[i] ? s : String(pos[i])));
  }, [pos[0], pos[1], pos[2]]);

  useEffect(() => {
    setEulerStrs(prev => prev.map((s, i) => eulerFocusedRef.current[i] ? s : String(euler[i])));
  }, [euler[0], euler[1], euler[2]]);

  useEffect(() => {
    setQuatStrs(prev => prev.map((s, i) => quatFocusedRef.current[i] ? s : String(quat[i])));
  }, [quat[0], quat[1], quat[2], quat[3]]);

  // syncTick 变化时（复位操作），强制同步所有输入框显示值
  useEffect(() => {
    setPosStrs([String(pos[0]), String(pos[1]), String(pos[2])]);
    setEulerStrs([String(euler[0]), String(euler[1]), String(euler[2])]);
    setQuatStrs([String(quat[0]), String(quat[1]), String(quat[2]), String(quat[3])]);
  }, [syncTick]);

  // All hooks are above. Now safe to early-return.
  if (!mj) return null;
  const { model, data, M } = mj;

  // 失焦时提交：解析字符串 → 写入 qpos → 通知更新
  const commitPos = (idx: number) => {
    posFocusedRef.current[idx] = false;
    const val = parseFloat(posStrs[idx]);
    if (!Number.isFinite(val)) { setPosStrs(prev => { const n = [...prev]; n[idx] = String(pos[idx]); return n; }); return; }
    if (!mj) return;
    data.qpos[addr + idx] = val;
    M.mj_forward(model, data);
    setPosStrs(prev => { const n = [...prev]; n[idx] = String(val); return n; });
    const nextPos = [...pos] as [number, number, number];
    nextPos[idx] = val;
    onChanged({ pos: nextPos, quat: [...quat] as [number, number, number, number] });
    onBlurCommit?.();
  };

  const commitEuler = (idx: number) => {
    eulerFocusedRef.current[idx] = false;
    const val = parseFloat(eulerStrs[idx]);
    if (!Number.isFinite(val)) { setEulerStrs(prev => { const n = [...prev]; n[idx] = String(euler[idx]); return n; }); return; }
    if (!mj) return;
    const newEuler: [number, number, number] = eulerStrs.map((s, i) => i === idx ? val : parseFloat(s)) as [number, number, number];
    for (let i = 0; i < 3; i++) { if (!Number.isFinite(newEuler[i])) newEuler[i] = euler[i]; }
    const newQuat = eulerXYZToQuat(newEuler);
    data.qpos[addr + 3] = newQuat[3]; // W
    data.qpos[addr + 4] = newQuat[0]; // X
    data.qpos[addr + 5] = newQuat[1]; // Y
    data.qpos[addr + 6] = newQuat[2]; // Z
    M.mj_forward(model, data);
    setEulerStrs(newEuler.map(String));
    setQuatStrs([newQuat[0], newQuat[1], newQuat[2], newQuat[3]].map(String));
    onChanged({ pos: [...pos] as [number, number, number], quat: [newQuat[0], newQuat[1], newQuat[2], newQuat[3]] });
    onBlurCommit?.();
  };

  const commitQuat = (idx: number) => {
    quatFocusedRef.current[idx] = false;
    const val = parseFloat(quatStrs[idx]);
    if (!Number.isFinite(val)) { setQuatStrs(prev => { const n = [...prev]; n[idx] = String(quat[idx]); return n; }); return; }
    if (!mj) return;
    const qposIdx = idx === 3 ? addr + 3 : addr + idx + 4;
    data.qpos[qposIdx] = val;
    M.mj_forward(model, data);
    let newQuat: [number, number, number, number] = [data.qpos[addr + 4], data.qpos[addr + 5], data.qpos[addr + 6], data.qpos[addr + 3]];
    // 归一化四元数，禁止零四元数
    const norm = Math.hypot(newQuat[0], newQuat[1], newQuat[2], newQuat[3]);
    if (norm > 1e-10) {
      newQuat = newQuat.map(v => v / norm) as [number, number, number, number];
      // 把归一化后的四元数写回 data.qpos
      data.qpos[addr + 3] = newQuat[3]; // W
      data.qpos[addr + 4] = newQuat[0]; // X
      data.qpos[addr + 5] = newQuat[1]; // Y
      data.qpos[addr + 6] = newQuat[2]; // Z
      M.mj_forward(model, data);
    } else {
      // 零四元数 fallback：使用当前四元数或单位四元数
      newQuat = [...quat] as [number, number, number, number];
      const fallbackNorm = Math.hypot(newQuat[0], newQuat[1], newQuat[2], newQuat[3]);
      if (fallbackNorm < 1e-10) {
        newQuat = [0, 0, 0, 1] as [number, number, number, number];
      } else {
        newQuat = newQuat.map(v => v / fallbackNorm) as [number, number, number, number];
      }
      data.qpos[addr + 3] = newQuat[3]; // W
      data.qpos[addr + 4] = newQuat[0]; // X
      data.qpos[addr + 5] = newQuat[1]; // Y
      data.qpos[addr + 6] = newQuat[2]; // Z
      M.mj_forward(model, data);
    }
    const newEuler = quatToEulerXYZ(newQuat);
    setQuatStrs([newQuat[0], newQuat[1], newQuat[2], newQuat[3]].map(String));
    setEulerStrs(newEuler.map(String));
    onChanged({ pos: [...pos] as [number, number, number], quat: [newQuat[0], newQuat[1], newQuat[2], newQuat[3]] });
    onBlurCommit?.();
  };

  // 步进调整：立即生效（不需要等失焦）
  const stepPos = (idx: number, delta: number) => {
    if (!mj) return;
    const cur = parseFloat(posStrs[idx]) || 0;
    const val = cur + delta;
    data.qpos[addr + idx] = val;
    M.mj_forward(model, data);
    setPosStrs(prev => { const n = [...prev]; n[idx] = String(val); return n; });
    const nextPos = [...pos] as [number, number, number];
    nextPos[idx] = val;
    onChanged({ pos: nextPos, quat: [...quat] as [number, number, number, number] });
    onBlurCommit?.();
  };

  const stepEuler = (idx: number, delta: number) => {
    if (!mj) return;
    const curEuler: [number, number, number] = eulerStrs.map(s => parseFloat(s) || 0) as [number, number, number];
    for (let i = 0; i < 3; i++) { if (!Number.isFinite(curEuler[i])) curEuler[i] = euler[i]; }
    curEuler[idx] += delta;
    const newQuat = eulerXYZToQuat(curEuler);
    data.qpos[addr + 3] = newQuat[3];
    data.qpos[addr + 4] = newQuat[0];
    data.qpos[addr + 5] = newQuat[1];
    data.qpos[addr + 6] = newQuat[2];
    M.mj_forward(model, data);
    setEulerStrs(curEuler.map(String));
    setQuatStrs([newQuat[0], newQuat[1], newQuat[2], newQuat[3]].map(String));
    onChanged({ pos: [...pos] as [number, number, number], quat: [newQuat[0], newQuat[1], newQuat[2], newQuat[3]] });
    onBlurCommit?.();
  };

  const stepQuat = (idx: number, delta: number) => {
    if (!mj) return;
    const curQuat = quatStrs.map(s => parseFloat(s) || 0);
    for (let i = 0; i < 4; i++) { if (!Number.isFinite(curQuat[i])) curQuat[i] = quat[i]; }
    curQuat[idx] += delta;
    // 归一化，禁止零四元数
    const norm = Math.hypot(curQuat[0], curQuat[1], curQuat[2], curQuat[3]);
    if (norm > 1e-10) {
      for (let i = 0; i < 4; i++) curQuat[i] /= norm;
    } else {
      // 零四元数 fallback：使用当前四元数或单位四元数
      const fallback = [...quat] as [number, number, number, number];
      const fallbackNorm = Math.hypot(fallback[0], fallback[1], fallback[2], fallback[3]);
      if (fallbackNorm > 1e-10) {
        for (let i = 0; i < 4; i++) curQuat[i] = fallback[i] / fallbackNorm;
      } else {
        curQuat[0] = 0; curQuat[1] = 0; curQuat[2] = 0; curQuat[3] = 1;
      }
    }
    data.qpos[addr + 3] = curQuat[3]; // W
    data.qpos[addr + 4] = curQuat[0]; // X
    data.qpos[addr + 5] = curQuat[1]; // Y
    data.qpos[addr + 6] = curQuat[2]; // Z
    M.mj_forward(model, data);
    const newEuler = quatToEulerXYZ([curQuat[0], curQuat[1], curQuat[2], curQuat[3]]);
    setQuatStrs(curQuat.map(String));
    setEulerStrs(newEuler.map(String));
    onChanged({ pos: [...pos] as [number, number, number], quat: [curQuat[0], curQuat[1], curQuat[2], curQuat[3]] as [number, number, number, number] });
    onBlurCommit?.();
  };

  return (
    <div className="border-b border-slate-100 bg-white">
      <div className="px-5 py-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-slate-800">Base Control</span>
            <span className="text-[10px] text-slate-400">帧 {currentFrame + 1}</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={onResetPos}
              className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded text-[10px] font-bold transition-colors border border-slate-200"
              title="复位位置到初始值"
            >
              复位位置
            </button>
            <button
              onClick={onResetRot}
              className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded text-[10px] font-bold transition-colors border border-slate-200"
              title="复位旋转到初始值"
            >
              复位旋转
            </button>
            <button
              onClick={onResetAll}
              className="px-2 py-1 bg-brand/10 hover:bg-brand/20 text-brand rounded text-[10px] font-bold transition-colors border border-brand/30"
              title="复位全部到初始值"
            >
              复位全部
            </button>
          </div>
        </div>

        {/* Position XYZ */}
        <div className="grid grid-cols-3 gap-x-3 gap-y-1.5 mb-3">
          {([
            ['Pos X', 0, 0.01],
            ['Pos Y', 1, 0.01],
            ['Pos Z', 2, 0.01],
          ] as const).map(([label, idx, step]) => (
            <div key={label} className="flex items-center gap-1">
              <span className="text-[11px] font-medium text-slate-600 w-10 shrink-0">{label}</span>
              <button
                onClick={() => stepPos(idx, -step)}
                className="w-5 h-5 flex items-center justify-center bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded text-slate-500 text-[10px] font-bold select-none shrink-0 transition-colors"
              >−</button>
              <input
                type="text"
                inputMode="decimal"
                value={posStrs[idx]}
                onChange={(e) => setPosStrs(prev => { const n = [...prev]; n[idx] = e.target.value; return n; })}
                onFocus={() => { posFocusedRef.current[idx] = true; }}
                onBlur={() => commitPos(idx)}
                className="min-w-0 flex-1 text-xs font-mono bg-slate-50 border border-slate-200 rounded px-1.5 py-1 outline-none focus:border-brand text-slate-700 text-center"
              />
              <button
                onClick={() => stepPos(idx, step)}
                className="w-5 h-5 flex items-center justify-center bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded text-slate-500 text-[10px] font-bold select-none shrink-0 transition-colors"
              >+</button>
            </div>
          ))}
        </div>

        {/* Euler Angles */}
        <div className="mb-3">
          <span className="text-[10px] font-bold text-slate-500 block mb-1.5">欧拉角 (rad)</span>
          <div className="grid grid-cols-3 gap-x-3 gap-y-1.5">
            {([
              ['Roll', 0, 0.05],
              ['Pitch', 1, 0.05],
              ['Yaw', 2, 0.05],
            ] as const).map(([label, idx, step]) => (
              <div key={label} className="flex items-center gap-1">
                <span className="text-[11px] font-medium text-slate-600 w-10 shrink-0">{label}</span>
                <button
                  onClick={() => stepEuler(idx, -step)}
                  className="w-5 h-5 flex items-center justify-center bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded text-slate-500 text-[10px] font-bold select-none shrink-0 transition-colors"
                >−</button>
                <input
                  type="text"
                  inputMode="decimal"
                  value={eulerStrs[idx]}
                  onChange={(e) => setEulerStrs(prev => { const n = [...prev]; n[idx] = e.target.value; return n; })}
                  onFocus={() => { eulerFocusedRef.current[idx] = true; }}
                  onBlur={() => commitEuler(idx)}
                  className="min-w-0 flex-1 text-[10px] font-mono bg-slate-50 border border-slate-200 rounded px-1.5 py-1 outline-none focus:border-brand text-slate-700 text-center"
                />
                <button
                  onClick={() => stepEuler(idx, step)}
                  className="w-5 h-5 flex items-center justify-center bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded text-slate-500 text-[10px] font-bold select-none shrink-0 transition-colors"
                >+</button>
              </div>
            ))}
          </div>
        </div>

        {/* Quaternion XYZW — 2x2 layout for wider inputs */}
        <div>
          <span className="text-[10px] font-bold text-slate-500 block mb-1.5">四元数</span>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
            {([
              ['X', 0, 0.01],
              ['Y', 1, 0.01],
              ['Z', 2, 0.01],
              ['W', 3, 0.01],
            ] as const).map(([label, idx, step]) => (
              <div key={label} className="flex items-center gap-1">
                <span className="text-[11px] font-medium text-slate-600 w-6 shrink-0">{label}</span>
                <button
                  onClick={() => stepQuat(idx, -step)}
                  className="w-5 h-5 flex items-center justify-center bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded text-slate-500 text-[10px] font-bold select-none shrink-0 transition-colors"
                >−</button>
                <input
                  type="text"
                  inputMode="decimal"
                  value={quatStrs[idx]}
                  onChange={(e) => setQuatStrs(prev => { const n = [...prev]; n[idx] = e.target.value; return n; })}
                  onFocus={() => { quatFocusedRef.current[idx] = true; }}
                  onBlur={() => commitQuat(idx)}
                  className="min-w-0 flex-1 text-[10px] font-mono bg-slate-50 border border-slate-200 rounded px-1.5 py-1 outline-none focus:border-brand text-slate-600 text-center"
                />
                <button
                  onClick={() => stepQuat(idx, step)}
                  className="w-5 h-5 flex items-center justify-center bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded text-slate-500 text-[10px] font-bold select-none shrink-0 transition-colors"
                >+</button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

var danceDurations:number = 0;
// ===== Constants =====
const FPS = 30;
const DURATION_SEC = 45;
const TOTAL_FRAMES = DURATION_SEC * FPS; // 1350
const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 2];
const BASE_PPF = 4; // base pixels per frame

const EMPTY_SIMULATION_FRAMES: JointAngles[] = [];
/** 地板 Y（MuJoCo z=0 对应此值）。值越大地板越靠近机器人 */
const FLOOR_Y = -0.2;

/** 调试开关：禁用 rootTransform 的 position/quaternion，只应用关节角 */
const DEBUG_DISABLE_ROOT_TRANSFORM = false;
const DEBUG_MARKERS = false;
/** 校准基座高度：固定 URDF root 的 Y 偏移，避免直接用 CSV root_pos_z 导致浮空。
 *  取值 0.1 对应 MujocoViewer 中站立姿态的典型高度（FLOOR_Y + root_pos_z + ROBOT_Y_OFFSET） */
const CALIBRATED_BASE_HEIGHT = 0.1;
/** 视觉脚底贴地校正：每次更新后自动把机器人最低点对齐到地面 */
const ENABLE_VISUAL_GROUNDING = false;


// ===== URDF Robot Loader =====
type UrdfJoint = {
  urdfName?: string;
  name?: string;
  axis?: THREE.Vector3;
  setJointValue?: (...values: (number | null)[]) => boolean;
  jointValue?: number[];
  type?: string;
};

type RobotInstance = {
  root: THREE.Object3D | null;
  joints: Record<string, UrdfJoint>;
  jointMap: Partial<Record<JointKey, string>>;
};

function createRobotInstance(config: RobotModelConfig): RobotInstance {
  return {
    root: null,
    joints: {},
    jointMap: { ...config.jointMap },
  };
}

function RobotModelLoader({
  joints,
  modelId,
  jointNameMap,
  rootTransform,
  refRootPos,
}: {
  joints: JointAngles;
  modelId: string;
  /** 动态关节映射：JointKey → URDF 关节名。来自 CSV 数据字段名的反向映射。不传则回退到 config.jointMap */
  jointNameMap?: Partial<Record<JointKey, string>>;
  // 浮动基座变换，用于驱动全身位移/旋转（对应 CSV 中 root_pos_*/root_rot_* 字段）
  rootTransform?: RootTransform | null;
  /** CSV 第 0 帧的 root_pos [x, y, z] 作为基准，当前帧与之的差值加权后应用到画面位移 */
  refRootPos?: [number, number, number] | null;
}) {
  const groupRef = useRef<THREE.Group | null>(null);
  const robotRef = useRef<RobotInstance>(createRobotInstance(getRobotModelConfig(modelId)));
  const { invalidate } = useThree();
  const config = getRobotModelConfig(modelId);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [root, setRoot] = useState<THREE.Object3D | null>(null);

  // URDF 加载后计算的脚底到 root 的距离（正值 = root 在脚底上方多少米）
  const footToRootYRef = useRef<number>(0.3);

  // [DIAG] 追踪 foot link 对象引用
  const footRefs = useRef<THREE.Object3D[]>([]);
  const diagFrameCount = useRef(0);

  // 渲染时实际使用的关节映射：优先用 CSV 动态映射，否则回退到模型配置的 jointMap
  const effectiveJointMap = useMemo<Partial<Record<JointKey, string>>>(
    () => (jointNameMap && Object.keys(jointNameMap).length > 0 ? jointNameMap : config.jointMap),
    [jointNameMap, config.jointMap],
  );

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    robotRef.current = createRobotInstance(config);

    const loader = new URDFLoader();
    const manager = new LoadingManager();
    manager.onError = (url: string) => console.error('[URDFLoader] asset error', url);
    loader.manager = manager;
    loader.packages = config.meshRoot;
    loader.fetchOptions = { mode: 'cors' };

    const handleLoaded = (loadedRoot: any) => {
      if (cancelled) return;

      const robot = robotRef.current;
      robot.root = loadedRoot;

      if (loadedRoot.joints) {
        for (const [jointName, jointObj] of Object.entries<UrdfJoint>(loadedRoot.joints)) {
          robot.joints[jointName] = jointObj;
        }
      }

      loadedRoot.scale.setScalar(config.defaultScale ?? 1);
      loadedRoot.rotation.set(
        config.defaultRotationX ?? 0,
        config.defaultRotationY ?? 0,
        config.defaultRotationZ ?? 0,
      );
      loadedRoot.position.set(0, 0, 0); // root 在 group 原点，绝对位置由 group 控制
      footToRootYRef.current = 0;

      // [DIAG] 查找脚部 link 对象（仅 foot link，排除 ankle joint）
      const feet: THREE.Object3D[] = [];
      const allNames: string[] = [];
      loadedRoot.traverse((obj: THREE.Object3D) => {
        if (obj.name) allNames.push(obj.name);
        const name = (obj.name || '').toLowerCase();
        if (name.includes('foot') && !name.includes('_joint')) {
          feet.push(obj);
        }
      });
      footRefs.current = feet;
      console.log('[DIAG RobotModelLoader] foot/ankle/leg names:', allNames.filter(n => n.includes('foot') || n.includes('ankle') || n.includes('leg') || n.includes('toe')));
      console.log('[DIAG RobotModelLoader] foot links found:', feet.map(f => f.name), 'total:', feet.length);

      setRoot(loadedRoot);
      console.log('[URDFLoader] robot ready, joints:', Object.keys(robot.joints).length, 'feet found:', feet.length);
    };

    const handleError = (err: unknown) => {
      if (cancelled) return;
      console.error('[URDFLoader] load error:', err);
      const message = err instanceof Error ? err.message : String(err);
      setLoadError(message || 'Failed to load URDF');
    };

    try {
      loader.load(config.urdfPath, handleLoaded, undefined, handleError);
    } catch (err) {
      handleError(err);
    }

    return () => {
      cancelled = true;
    };
  }, [
    modelId,
    config.id,
    config.urdfPath,
    config.meshRoot,
    config.defaultScale,
    config.defaultRotationX,
    config.defaultRotationY,
    config.defaultRotationZ,
  ]);

  useEffect(() => {
    const group = groupRef.current;
    if (!group || !root) return;

    group.add(root);

    // 调试 marker：robot root origin（绿色小球，挂在 robot.root 下）
    if (DEBUG_MARKERS) {
      const rootMarker = new THREE.Mesh(
        new THREE.SphereGeometry(0.05, 16, 16),
        new THREE.MeshBasicMaterial({ color: 0x00ff00 }),
      );
      rootMarker.name = '__debug_root_marker__';
      root.add(rootMarker);
    }

    return () => {
      if (root.parent === group) {
        group.remove(root);
      }
      if (DEBUG_MARKERS) {
        const existing = root.getObjectByName('__debug_root_marker__');
        if (existing) root.remove(existing);
      }
    };
  }, [root]);

  // 响应 joints prop 变化，直接更新 URDF 机器人关节并触发场景重绘
  useEffect(() => {
    const robot = robotRef.current;
    if (!robot.root) return;

    // 调试模式：禁用 rootTransform，只应用关节角
    if (DEBUG_DISABLE_ROOT_TRANSFORM) {
      // 固定 group 位置：水平居中，高度用校准值
      if (groupRef.current) {
        groupRef.current.position.set(0, CALIBRATED_BASE_HEIGHT, 0);
      }
      // 恢复 root 默认旋转
      const defaultQuat = new THREE.Quaternion().setFromEuler(
        new THREE.Euler(
          config.defaultRotationX ?? 0,
          config.defaultRotationY ?? 0,
          config.defaultRotationZ ?? 0,
        ),
      );
      robot.root.quaternion.copy(defaultQuat);
      // 固定 root 本地位置（脚底到 root 的固定偏移）
      robot.root.position.set(0, footToRootYRef.current, 0);
    } else {
      // // 坐标系修正四元数（MuJoCo Z-up → Three.js Y-up）
      // const defaultQuat = new THREE.Quaternion().setFromEuler(
      //   new THREE.Euler(
      //     config.defaultRotationX ?? 0,
      //     config.defaultRotationY ?? 0,
      //     config.defaultRotationZ ?? 0,
      //   ),
      // );
      // 坐标系修正四元数（MuJoCo Z-up → Three.js Y-up）
      const defaultQuat = new THREE.Quaternion().setFromEuler(
        new THREE.Euler(
          config.defaultRotationX ?? 0,
          config.defaultRotationY ?? 0,
          config.defaultRotationZ ?? 0,
        ),
      );
      // 1. 应用浮动基座变换（root_pos_*/root_rot_*）
      if (rootTransform) {
        const px = Number(rootTransform.pos[0] ?? 0);
        const py = Number(rootTransform.pos[1] ?? 0);
        const pz = Number(rootTransform.pos[2] ?? 0);

        // MuJoCo 坐标系 → Three.js 坐标系：位置和朝向使用同一机型配置变换
        robot.root.position.copy(new THREE.Vector3(px, py, pz).applyQuaternion(defaultQuat));

        // 叠加 MuJoCo 绝对 root rotation
        const [qx, qy, qz, qw] = rootTransform.quat;
        if (qw !== 0 || rootTransform.quat.some((v) => v !== 0)) {
          const csvQuat = new THREE.Quaternion(qx, qy, qz, qw).normalize();
          robot.root.quaternion.copy(defaultQuat).multiply(csvQuat);
        } else {
          robot.root.quaternion.copy(defaultQuat);
        }

        // [DIAG] 对比 RobotModelLoader 收到的 rootTransform 与应用的 root 位姿
        console.log('[DIAG RobotModelLoader] rootTransform.pos(xyz):',
          px.toFixed(4), py.toFixed(4), pz.toFixed(4),
          '| quat(xyzw):', qx.toFixed(4), qy.toFixed(4), qz.toFixed(4), qw.toFixed(4));
        console.log('[DIAG RobotModelLoader] → root.position(xyz):',
          robot.root.position.x.toFixed(4), robot.root.position.y.toFixed(4), robot.root.position.z.toFixed(4),
          '| root.quat(xyzw):',
          robot.root.quaternion.x.toFixed(4), robot.root.quaternion.y.toFixed(4),
          robot.root.quaternion.z.toFixed(4), robot.root.quaternion.w.toFixed(4));
      } else {
        robot.root.quaternion.copy(defaultQuat);
        robot.root.position.set(0, footToRootYRef.current, 0);
      }

      // 2. group 仅用于地面高度校准（不再携带水平位移）
      if (groupRef.current) {
        groupRef.current.position.set(0, CALIBRATED_BASE_HEIGHT, 0);
      }
    }

    // 2. 应用关节角度
    const map = effectiveJointMap;
    let updated = false;
    (Object.keys(map) as JointKey[]).forEach((uiKey) => {
      const urdfName = map[uiKey];
      if (!urdfName) return;
      const joint = robot.joints[urdfName];
      if (!joint || typeof joint.setJointValue !== 'function') return;

      const val = Number(joints[uiKey]) || 0;
      if (joint.setJointValue(val * Math.PI / 180)) {
        updated = true;
      }
    });

    // 3. 视觉脚底贴地校正（仅在 ENABLE_VISUAL_GROUNDING 时启用）
    // 注意：必须在关节 setJointValue 之后调用，因为脚的位置会随关节角变化
    if (ENABLE_VISUAL_GROUNDING && groupRef.current && robot.root) {
      robot.root.updateMatrixWorld(true);
      const box = new THREE.Box3().setFromObject(robot.root);
      if (Number.isFinite(box.min.y)) {
        const deltaY = FLOOR_Y - box.min.y;
        groupRef.current.position.y += deltaY;
      }
    }

    // 调试模式：在关节应用后也重置 root 位置，确保完全隔离 rootTransform
    if (DEBUG_DISABLE_ROOT_TRANSFORM) {
      if (groupRef.current) {
        groupRef.current.position.set(0, CALIBRATED_BASE_HEIGHT, 0);
      }
      const defaultQuat = new THREE.Quaternion().setFromEuler(
        new THREE.Euler(
          config.defaultRotationX ?? 0,
          config.defaultRotationY ?? 0,
          config.defaultRotationZ ?? 0,
        ),
      );
      robot.root.quaternion.copy(defaultQuat);
      robot.root.position.set(0, footToRootYRef.current, 0);
    }

    // [DIAG] 每5帧打印脚部世界位置，观察支撑脚 Y 是否恒定
    diagFrameCount.current += 1;
    if (diagFrameCount.current % 5 === 0 && robot.root) {
      robot.root.updateMatrixWorld();
      const worldPos = new THREE.Vector3();
      for (const foot of footRefs.current) {
        foot.getWorldPosition(worldPos);
        console.log(`[DIAG foot RL] frame:${diagFrameCount.current} ${foot.name || '(unnamed)'} world:(${worldPos.x.toFixed(4)}, ${worldPos.y.toFixed(4)}, ${worldPos.z.toFixed(4)})`);
      }
    }

    // 根变换 or 关节变化都应该触发重绘
    if (updated || rootTransform) invalidate();
  }, [joints, invalidate, effectiveJointMap, rootTransform, config.defaultScale]);

  // 首次加载后立即应用初始姿态
  useEffect(() => {
    const robot = robotRef.current;
    if (!robot.root) return;

    const map = effectiveJointMap;
    (Object.keys(map) as JointKey[]).forEach((uiKey) => {
      const urdfName = map[uiKey];
      if (!urdfName) return;
      const joint = robot.joints[urdfName];
      if (!joint || typeof joint.setJointValue !== 'function') return;

      const val = Number(joints[uiKey]) || 0;
      joint.setJointValue(val * Math.PI / 180);
    });

    // 调试模式：在首次加载后也重置 root 位置
    if (DEBUG_DISABLE_ROOT_TRANSFORM) {
      if (groupRef.current) {
        groupRef.current.position.set(0, CALIBRATED_BASE_HEIGHT, 0);
      }
      const defaultQuat = new THREE.Quaternion().setFromEuler(
        new THREE.Euler(
          config.defaultRotationX ?? 0,
          config.defaultRotationY ?? 0,
          config.defaultRotationZ ?? 0,
        ),
      );
      robot.root.quaternion.copy(defaultQuat);
      robot.root.position.set(0, footToRootYRef.current, 0);
    }

    invalidate();
  }, [root, invalidate, effectiveJointMap]);

  return (
    <group ref={(node) => { groupRef.current = node; }}>
      {/* 调试 marker：group origin（红色小球） */}
      {DEBUG_MARKERS && (
        <mesh>
          <sphereGeometry args={[0.05, 16, 16]} />
          <meshBasicMaterial color="#ff0000" />
        </mesh>
      )}
      {/* 调试 marker：world origin（蓝色小球，固定在世界坐标 FLOOR_Y 处） */}
      {DEBUG_MARKERS && (
        <mesh position={[0, FLOOR_Y, 0]}>
          <sphereGeometry args={[0.05, 16, 16]} />
          <meshBasicMaterial color="#0000ff" />
        </mesh>
      )}
      {/* 调试 marker：robot root（挂在 robot.root 下，由 useEffect 添加绿色小球） */}
      {loadError && (
        <mesh>
          <boxGeometry args={[0.4, 0.4, 0.4]} />
          <meshStandardMaterial color="#EF4444" />
        </mesh>
      )}
    </group>
  );
}

// MuJoCo checkerboard 参数
const CHECKER_RGB1 = '#334d66'; // rgb1=".2 .3 .4"
const CHECKER_RGB2 = '#1a2633'; // rgb2=".1 0.15 0.2"
const CHECKER_MARK = '#cccccc'; // markrgb=".8 .8 .8"
const CHECKER_SIZE = 512;       // width/height

/** 生成 MuJoCo checkerboard 纹理（含十字标记） */
function createCheckerTexture(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas'); 
  canvas.width = CHECKER_SIZE;
  canvas.height = CHECKER_SIZE;
  const ctx = canvas.getContext('2d')!;

  const gridCount = 10;
  const cellSize = CHECKER_SIZE / gridCount;

  // 绘制棋盘格
  for (let row = 0; row < gridCount; row++) {
    for (let col = 0; col < gridCount; col++) {
      ctx.fillStyle = (row + col) % 2 === 0 ? CHECKER_RGB1 : CHECKER_RGB2;
      ctx.fillRect(col * cellSize, row * cellSize, cellSize, cellSize);
    }
  }

  // 绘制十字标记（每隔一格）
  ctx.strokeStyle = CHECKER_MARK;
  ctx.lineWidth = 2;
  const crossLen = cellSize * 0.15;
  for (let row = 1; row < gridCount; row += 2) {
    for (let col = 1; col < gridCount; col += 2) {
      const cx = col * cellSize;
      const cy = row * cellSize;
      ctx.beginPath();
      ctx.moveTo(cx - crossLen, cy);
      ctx.lineTo(cx + crossLen, cy);
      ctx.moveTo(cx, cy - crossLen);
      ctx.lineTo(cx, cy + crossLen);
      ctx.stroke();
    }
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(4, 4); // texrepeat="1 1" → 对应 4×4 平铺实现合理密度
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

function RobotFigure({ joints, controlsRef, modelId, jointNameMap, rootTransform, refRootPos, onCanvasReady }: {
  joints: JointAngles;
  controlsRef: React.RefObject<any>;
  modelId: string;
  jointNameMap?: Partial<Record<JointKey, string>>;
  rootTransform?: RootTransform | null;
  refRootPos?: [number, number, number] | null;
  onCanvasReady?: (canvas: HTMLCanvasElement) => void;
}) {
  const config = getRobotModelConfig(modelId);
  const checkerTex = useMemo(() => createCheckerTexture(), []);
  return (
    <div className="w-full h-full min-h-[420px] rounded-2xl overflow-hidden border border-white/10 bg-slate-950/40 relative">
      <Canvas
        camera={{ position: config.defaultCameraPosition ?? [0, 0.25, 2.2], fov: 45 }}
        dpr={[1, 1.25]}
        gl={{
          antialias: true,
          powerPreference: 'high-performance',
          failIfMajorPerformanceCaveat: false,
          alpha: false,
          stencil: false,
          depth: true,
          preserveDrawingBuffer: true,
        }}
        onCreated={({ gl }) => {
          gl.toneMapping = THREE.ACESFilmicToneMapping;
          gl.toneMappingExposure = 1.0;
          onCanvasReady?.(gl.domElement);
        }}
      >
        <ambientLight intensity={0.55} />
        <directionalLight position={[5, 6, 6]} intensity={1.1} />
        <directionalLight position={[-4, 2, 3]} intensity={0.45} color="#7c93ff" />
        <Environment preset="city" />
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, FLOOR_Y, 0]} receiveShadow>
          <planeGeometry args={[40, 40]} />
          <meshStandardMaterial
            map={checkerTex}
            roughness={0.7}
            metalness={0}
            envMapIntensity={0.5}
          />
        </mesh>
        <ContactShadows
          position={[0, FLOOR_Y + 0.002, 0]}
          scale={5}
          far={1.8}
          blur={2.5}
          opacity={0.55}
          resolution={512}
          frames={1}
        />
        <RobotModelLoader joints={joints} modelId={modelId} jointNameMap={jointNameMap} rootTransform={rootTransform} refRootPos={refRootPos} />
        <OrbitControls ref={controlsRef} enablePan={true} enableZoom={true} minDistance={1.4} maxDistance={4} />
      </Canvas>
    </div>
  );
}


// ===== Main Keyframe Editor =====
export default function KeyframeEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { dances, keyframeIntervals, addKeyframeInterval, updateKeyframeInterval, deleteKeyframeInterval, clearKeyframeIntervals } = useStore();
  const [selectedIntervalId, setSelectedIntervalId] = useState<string | null>(null);
  const [loadingTask, setLoadingTask] = useState(false);
  const [taskDetail, setTaskDetail] = useState<TaskDetailResponse | null>(null);
  const controlsRef = useRef<any>(null);
  const [simulationFrames, setSimulationFrames] = useState<JointAngles[]>(EMPTY_SIMULATION_FRAMES);
  /** CSV 原始帧（可变基准，用于 unsavedFrameIndices 对比） */
  const originalFramesRef = useRef<JointAngles[]>([]);
  /** rootTransforms 保存基准（可变，用于 dirty 对比） */
  const originalRootTransformsRef = useRef<RootTransform[]>([]);
  /** 递增版本号，每次更新 originalFramesRef 时同步递增，触发 useMemo 重算 */
  const [originalFramesVersion, setOriginalFramesVersion] = useState(0);
  const bumpOriginalFramesVersion = () => setOriginalFramesVersion(v => v + 1);
  /** 跟踪滑块是否正在拖动，用于在拖动开始时推一次 undo 快照 */
  const jointDragActiveRef = useRef(false);
  const endJointEdit = useCallback(() => { jointDragActiveRef.current = false; }, []);
  /** 跟踪 Base Control 是否正在编辑，用于在编辑开始时推一次 undo 快照 */
  const baseEditActiveRef = useRef(false);
  /** CSV 最初加载时的原始帧（不可变，用于"重置所有帧"回到最初状态） */
  const csvOriginalFramesRef = useRef<JointAngles[]>([]);
  /** 服务端已持久化的已编辑帧序号集合（来自 edit-set 接口） */
  const [editedFrameSet, setEditedFrameSet] = useState<Set<number>>(new Set());
  /** 最近一次保存时的关键帧帧序号（用于时间轴琥珀色标记，区分"仅插值经过"和"真修改"） */
  const [persistedKeyframeFrames, setPersistedKeyframeFrames] = useState<Set<number>>(new Set());
  /** 当前选中的机器人机型 ID（必须在 dance useMemo 之前声明） */
  const [selectedRobotModelId, setSelectedRobotModelId] = useState<string>(DEFAULT_ROBOT_MODEL_ID);
  /** 根据当前机型获取配置 */
  const currentRobotModel = useMemo(() => getRobotModelConfig(selectedRobotModelId), [selectedRobotModelId]);
  // taskDetail.robot_model 变化时自动同步 selectedRobotModelId
  useEffect(() => {
    if (taskDetail?.robot_model) {
      const config = ROBOT_MODELS.find(
        (m) => m.id === taskDetail.robot_model ||
          (m.headingKey && m.headingKey === taskDetail.robot_model),
      );
      if (config) {
        setSelectedRobotModelId(config.id);
      }
    }
  }, [taskDetail?.robot_model]);
  /** 根据当前机型获取 fieldNames（用于 CSV 解析） */
  const currentFieldNames = useMemo(() => {
    const headingKey = currentRobotModel.headingKey;
    const headings = (tableHeadings as Record<string, string[]>)[headingKey];
    return headings && headings.length > 0 ? headings : [];
  }, [currentRobotModel]);
  const localDance = dances.find(d => d.id === id);
  const dance = useMemo(() => {
    if (taskDetail) {
      return {
        id: String(taskDetail.task_id ?? id ?? ''),
        name: taskDetail.task_name ?? localDance?.name ?? '未命名舞蹈',
        type: 'simulation' as const,
        status:
          taskDetail.status === 'success' || taskDetail.status === 'completed' || taskDetail.status === 'done'
            ? 'success'
            : taskDetail.status === 'failed' || taskDetail.status === 'error'
              ? 'failed'
              : taskDetail.status === 'training'
                ? 'training'
                : 'generating',
        duration: localDance?.duration ?? `${taskDetail.duration ?? 0}`,
        robotModel: taskDetail.robot_model ?? localDance?.robotModel ?? '未知设备',
        imageUrl: taskDetail.cover_url ?? localDance?.imageUrl ?? '',
        videoUrl: localDance?.videoUrl,
        createdAt: taskDetail.created_timestamp ?? localDance?.createdAt ?? '',
        progress: taskDetail.progress ?? localDance?.progress,
      };
    }
    return localDance ?? null;
  }, [taskDetail, localDance, id]);

  // --- State ---
  const playTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [currentFrame, setCurrentFrame] = useState(0);
  const currentFrameRef = useRef(currentFrame);
  // 暂停同步锁：暂停瞬间屏蔽 MujocoViewer 的旧帧回调，避免覆盖正确帧
  const pausingSyncRef = useRef(false);
  // 播放时直接操作DOM的ref，避免触发React重渲染
  const playbackSliderRef = useRef<HTMLInputElement | null>(null);
  const playbackFrameInputRef = useRef<HTMLInputElement | null>(null);
  const playbackTimeLabelRef = useRef<HTMLSpanElement | null>(null);
  const playbackHeadRef = useRef<HTMLDivElement | null>(null);
  const playbackHeadLabelRef = useRef<HTMLSpanElement | null>(null);
  // 播放期间低频同步 currentFrame state 到 React，避免重渲染时 slider 跳回旧值
  const lastStateSyncTimeRef = useRef(0);
  const displayFrame = currentFrame;
  // 获取 MujocoViewer 实际渲染的帧号（用于暂停时精确同步）
  const getMujocoFrameRef = useRef<(() => number) | null>(null);

  const [playing, setPlaying] = useState(false);
  // 非播放状态下同步 currentFrameRef，播放期间由 handlePlaybackFrame 更新 ref
  // 暂停锁期间不覆盖，避免把刚写入的正确帧改回旧 state
  useEffect(() => {
    if (!playing && !pausingSyncRef.current) {
      currentFrameRef.current = currentFrame;
    }
  }, [currentFrame, playing]);
  const [playAll, setPlayAll] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [speedDropdownOpen, setSpeedDropdownOpen] = useState(false);
  const lastToastTime = useRef<number>(0);

  const throttledToast = useCallback((type: ToastType, message: string) => {
    const now = Date.now();
    if (now - lastToastTime.current > 2000) {
      toast(type, message);
      lastToastTime.current = now;
    }
  }, []);
  const [unsaved, setUnsaved] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);

  // Panel expansions
  const [paramsExpanded, setParamsExpanded] = useState(true);

  // Joint curve panel state
  const [curveExpanded, setCurveExpanded] = useState(false);
  const curvePanelRef = useRef<HTMLDivElement>(null);
  const [curveJointKey, setCurveJointKey] = useState<JointKey | ''>('');

  // Timeline drag selection
  const [dragSelection, setDragSelection] = useState<{ start: number; end: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartFrame = useRef(0);

  // Interval edge resizing
  const [resizingEdge, setResizingEdge] = useState<{ intervalId: string; edge: 'start' | 'end' } | null>(null);

  // Keyframes for editing within intervals
  const [keyframes, setKeyframes] = useState<KeyframeData[]>([]);

  // 处理区间（0-based），默认全帧范围；与拖拽选择联动，四个功能按钮使用此区间
  const [processingRange, setProcessingRange] = useState<{ start: number; end: number }>({ start: 0, end: 0 });

  // Python processing loading states
  const [isAnkleLockedUprighting, setIsAnkleLockedUprighting] = useState<'pitch' | 'roll' | false>(false);
  const [isSmartFootAdjusting, setIsSmartFootAdjusting] = useState(false);
  const [isFootLeveling, setIsFootLeveling] = useState(false);
  const [isZmpChecking, setIsZmpChecking] = useState(false);
  const [isRestoringBackup, setIsRestoringBackup] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isExportingVideo, setIsExportingVideo] = useState(false);
  const [zmpReport, setZmpReport] = useState<{
    score: number;
    high_risk: number[];
    warning_risk: number[];
    by_direction: Record<string, number[]>;
    details: Array<{
      frame_idx: number;
      level: string;
      direction: string;
      zmp_margin: number;
      com_margin: number;
      zmp: number[];
      com: number[];
      reasons: string[];
    }>;
    worst_text: string;
    summary: string;
  } | null>(null);
  const [showZmpModal, setShowZmpModal] = useState(false);
  const [originalSimulationData, setOriginalSimulationData] = useState<SimulationJsonResponse | null>(null);
  /** CSV 字段 → JointKey 的动态反向映射，直接用于 3D 渲染驱动 URDF 关节 */
  const [jointKeyToUrdfName, setJointKeyToUrdfName] = useState<Partial<Record<JointKey, string>>>({});
  /** CSV 字段名列表（含 root 列），用于计算关节列索引 */
  const [csvFieldNames, setCsvFieldNames] = useState<string[]>([]);
  /** 仅保留当前模型实际拥有的关节（jointKeyToUrdfName 有映射的） */
  const activeJointDefs = useMemo(() => {
    const mappedKeys = new Set(Object.keys(jointKeyToUrdfName) as JointKey[]);
    return mappedKeys.size > 0 ? JOINT_DEFS.filter(d => mappedKeys.has(d.key)) : JOINT_DEFS;
  }, [jointKeyToUrdfName]);
  /** JointKey → CSV 关节列索引（0-based，不含 root 列），用于 Python 后端 selected_joint_indices */
  const jointKeyToCsvColIdx = useMemo(() => {
    const map: Partial<Record<JointKey, number>> = {};
    if (csvFieldNames.length === 0) return map;
    const jointFields = csvFieldNames.filter(f => !f.startsWith('root_') && f);
    jointFields.forEach((field, idx) => {
      for (const [jk, urdf] of Object.entries(jointKeyToUrdfName)) {
        if (urdf === field) {
          map[jk as JointKey] = idx;
          break;
        }
      }
    });
    return map;
  }, [csvFieldNames, jointKeyToUrdfName]);
  /** MuJoCo 模块引用，供 Base Control 面板直接操作 qpos */
  const [mjHandle, setMjHandle] = useState<{ model:any; data:any; M:any; qmap:Record<string,number> } | null>(null);
  const mjHandleRef = useRef<{ model:any; data:any; M:any; qmap:Record<string,number> } | null>(null);
  // 用 useCallback 稳定回调，避免 MujocoViewer 因 onMjReady 身份变化而重复初始化
  const handleMjReady = useCallback((mj: { model:any; data:any; M:any; qmap:Record<string,number> }) => {
    mjHandleRef.current = mj;
    setMjHandle(mj);
  }, []);
  const [tick, setTick] = useState(0);
  const [baseSyncTick, setBaseSyncTick] = useState(0);
  // 每帧的浮动基座变换（root_pos_*/root_rot_*），用于驱动全身位移/旋转 */
  const [rootTransforms, setRootTransforms] = useState<RootTransform[]>([]);
  /** CSV 最初加载时的 rootTransforms（不可变，用于"重置所有帧"恢复） */
  const csvOriginalRootTransformsRef = useRef<RootTransform[]>([]);
  /** CSV 最初加载时的 originalSimulationData（不可变，用于"重置所有帧"恢复） */
  const csvOriginalSimulationDataRef = useRef<SimulationJsonResponse | null>(null);
  /** 地面基准：取所有帧 root_pos_z 的中位数（参考 MuJoCo 逻辑中位数更稳定） */
  const refRootPos = useMemo<[number, number, number] | null>(() => {
    if (rootTransforms.length === 0) return null;
    const allZ = rootTransforms.map((rt) => rt.pos[2]);
    const sorted = [...allZ].sort((a, b) => a - b);
    const medianZ = sorted[Math.floor(sorted.length / 2)];
    return [rootTransforms[0].pos[0], rootTransforms[0].pos[1], medianZ];
  }, [rootTransforms]);

  // 写入 MuJoCo qpos 的 base free joint
  const writeMjBaseQpos = useCallback((mj: { model: any; data: any; M: any }, rt: RootTransform) => {
    const { model, data, M } = mj;
    let addr = 0;
    for (let i = 0; i < model.njnt; i++) {
      if (model.jnt_type[i] === 0) { addr = model.jnt_qposadr[i]; break; }
    }
    data.qpos[addr + 0] = rt.pos[0]; // x
    data.qpos[addr + 1] = rt.pos[1]; // y
    data.qpos[addr + 2] = rt.pos[2]; // z
    data.qpos[addr + 3] = rt.quat[3]; // w
    data.qpos[addr + 4] = rt.quat[0]; // x
    data.qpos[addr + 5] = rt.quat[1]; // y
    data.qpos[addr + 6] = rt.quat[2]; // z
    M.mj_forward(model, data);
  }, []);

  // ✅ 仿真数据加载完成标识：只有 frames 真正就绪后，才挂载 3D 机器人

  // History and Save logic
  const [baseline, setBaseline] = useState<{keyframes: KeyframeData[], intervals: KeyframeInterval[]} | null>(null);

  // Undo stack: saves full snapshots before destructive operations
  const hasUnsavedChanges = useCallback((
    frames: JointAngles[],
    roots: RootTransform[],
  ): boolean => {
    const savedFrames = originalFramesRef.current;
    const savedRoots = originalRootTransformsRef.current;

    if (frames.length !== savedFrames.length) return true;

    for (let i = 0; i < frames.length; i++) {
      const current = frames[i];
      const saved = savedFrames[i];
      if (!saved) return true;

      for (const key of Object.keys(current) as JointKey[]) {
        if (Math.abs((current[key] ?? 0) - (saved[key] ?? 0)) > 1e-6) {
          return true;
        }
      }

      const currentRoot = roots[i];
      const savedRoot = savedRoots[i];

      if (!!currentRoot !== !!savedRoot) return true;

      if (currentRoot && savedRoot) {
        if (
          currentRoot.pos.some(
            (value, axis) => Math.abs(value - savedRoot.pos[axis]) > 1e-6,
          ) ||
          currentRoot.quat.some(
            (value, axis) => Math.abs(value - savedRoot.quat[axis]) > 1e-6,
          )
        ) {
          return true;
        }
      }
    }

    return false;
  }, []);

  const undoStackRef = useRef<{
    simulationFrames: JointAngles[];
    rootTransforms: RootTransform[];
    keyframeIntervals: KeyframeInterval[];
    keyframes: KeyframeData[];
  }[]>([]);
  const MAX_UNDO = 20;

  const pushUndoSnapshot = useCallback(() => {
    const snapshot = {
      simulationFrames: simulationFrames.map(f => ({ ...f })),
      rootTransforms: rootTransforms.map(rt => ({
        pos: [...rt.pos] as [number, number, number],
        quat: [...rt.quat] as [number, number, number, number],
      })),
      keyframeIntervals: JSON.parse(JSON.stringify(keyframeIntervals)) as KeyframeInterval[],
      keyframes: keyframes.map(k => ({
        ...k,
        joints: { ...k.joints },
      })),
    };
    undoStackRef.current = [...undoStackRef.current.slice(-(MAX_UNDO - 1)), snapshot];
  }, [simulationFrames, rootTransforms, keyframeIntervals, keyframes]);

  const undoStep = useCallback(() => {
    const stack = undoStackRef.current;
    if (stack.length === 0) {
      toast('info', '没有可撤销的操作');
      return;
    }
    const snapshot = stack[stack.length - 1];
    undoStackRef.current = stack.slice(0, -1);

    // Deep-copy snapshot data into state (snapshot must not share objects with live state)
    setSimulationFrames(
      snapshot.simulationFrames.map(f => ({ ...f }))
    );
    setRootTransforms(
      snapshot.rootTransforms.map(rt => ({
        pos: [...rt.pos] as [number, number, number],
        quat: [...rt.quat] as [number, number, number, number],
      }))
    );

    // Full interval restore: delete current intervals not in snapshot, restore snapshot intervals
    const snapshotIds = new Set(snapshot.keyframeIntervals.map(int => int.id));
    // Delete intervals that exist now but not in snapshot
    keyframeIntervals.forEach(int => {
      if (!snapshotIds.has(int.id)) {
        deleteKeyframeInterval(int.id);
      }
    });
    // Restore/add snapshot intervals (deep-copy frames)
    snapshot.keyframeIntervals.forEach(int => {
      const current = keyframeIntervals.find(c => c.id === int.id);
      const clonedFrames = int.frames.map(f => ({ ...f }));
      if (current) {
        // Update existing interval with all fields
        updateKeyframeInterval(int.id, {
          name: int.name,
          startFrame: int.startFrame,
          endFrame: int.endFrame,
          color: int.color,
          frames: clonedFrames,
        });
      } else {
        // Re-add deleted interval
        addKeyframeInterval({ ...int, frames: clonedFrames });
      }
    });

    setKeyframes(
      snapshot.keyframes.map(k => ({
        ...k,
        joints: { ...k.joints },
      }))
    );

    // Compute displayFrames from restored simulationFrames + restored intervals
    const restoredDisplayFrames = snapshot.simulationFrames.map((f, i) => {
      let result = { ...f };
      for (const int of snapshot.keyframeIntervals) {
        if (i >= int.startFrame && i <= int.endFrame) {
          const offset = i - int.startFrame;
          if (offset < int.frames.length) {
            result = { ...int.frames[offset] };
          }
        }
      }
      return result;
    });

    // Dirty detection against save baseline (not csvOriginal)
    setUnsaved(hasUnsavedChanges(restoredDisplayFrames, snapshot.rootTransforms));

    toast('success', '已撤销上一步操作');
  }, [keyframeIntervals, updateKeyframeInterval, deleteKeyframeInterval, addKeyframeInterval, hasUnsavedChanges]);

  // Base Control 修改 → 写回 rootTransforms (declared after displayFrames below)

  // Base Control 复位回调 (declared after displayFrames below)

  // Initialize baseline
  useEffect(() => {
    if (!baseline && keyframeIntervals.length > 0) {
      setBaseline({ keyframes: [...keyframes], intervals: JSON.parse(JSON.stringify(keyframeIntervals)) });
    }
  }, [keyframeIntervals, keyframes, baseline]);

  // loadSimulationData 在 loadTaskDetail 中调用，不单独定义 useEffect

  const saveAll = async () => {
    // 构造提交数据：将 displayFrames 和 rootTransforms 克隆为已提交版本
    const committedFrames = displayFrames.map(frame => ({ ...frame }));
    const committedRoots = rootTransforms.map(rt => ({
      pos: [...rt.pos] as [number, number, number],
      quat: [...rt.quat] as [number, number, number, number],
    }));

    if (!originalSimulationData) {
      toast('warning', '原始仿真数据尚未加载完成');
      return;
    }

    const payload = buildSimulationPayloadFromFrames(originalSimulationData, committedFrames, jointKeyToUrdfName, committedRoots);

    // 调用后端 API
    const userId = api.getStoredUserId();
      const simulationId = taskDetail?.simulation_id;

    if (!simulationId) {
      toast('warning', '缺少 simulation_id');
      return;
    }

    try {
      await api.post(`/api/v1/simulation/${simulationId}/upload`, payload, {
        headers: { 'x-user-id': userId },
      });
    } catch (error) {
      console.error('Save all failed:', error);
      toast('error', '保存失败，请稍后重试');
      return;
    }

    // 保存成功：更新保存基准
    setOriginalSimulationData(payload);
    setSimulationFrames(committedFrames);
    setRootTransforms(committedRoots);
    originalFramesRef.current = committedFrames.map(f => ({ ...f }));
    originalRootTransformsRef.current = committedRoots.map(rt => ({
      pos: [...rt.pos] as [number, number, number],
      quat: [...rt.quat] as [number, number, number, number],
    }));
    bumpOriginalFramesVersion();
    setBaseline({
      keyframes: [...keyframes],
      intervals: JSON.parse(JSON.stringify(keyframeIntervals)),
    });
    setUnsaved(false);
    undoStackRef.current = [];
    jointDragActiveRef.current = false;
    baseEditActiveRef.current = false;
    // 将本次保存中相比原始CSV有变化的帧序号加入已编辑集合
    const csvOrig = csvOriginalFramesRef.current;
    if (csvOrig.length === committedFrames.length) {
      setEditedFrameSet(prev => {
        const next = new Set(prev);
        for (let i = 0; i < committedFrames.length; i++) {
          const cur = committedFrames[i];
          const orig = csvOrig[i];
          if (!orig) continue;
          for (const key of Object.keys(cur) as JointKey[]) {
            if (Math.abs((cur[key] ?? 0) - (orig[key] ?? 0)) > 1e-6) {
              next.add(i);
              break;
            }
          }
        }
        return next;
      });
    }
    // 记录本次保存时的关键帧位置（用于时间轴琥珀色标记，区分"仅插值经过"和"真修改"）
    const kfFrameSet = new Set<number>();
    for (const kf of keyframes) {
      if (kf.frame >= 0 && kf.frame < committedFrames.length) {
        kfFrameSet.add(kf.frame);
      }
    }
    setPersistedKeyframeFrames(kfFrameSet);

    toast('success', '已保存所有修改');
  };

  const saveCurrentFrame = async () => {
    // 数据边界检查
    const frame = displayFrames[displayFrame];
    const root = rootTransforms[displayFrame];

    if (!frame || !root) {
      toast('warning', '当前帧数据无效');
      return;
    }

    // 合并当前帧到 original baseline，构造完整 payload
    // 只修改当前帧，其它帧使用保存后的 baseline
    const nextFrames = originalFramesRef.current.map((f, i) =>
      i === displayFrame ? { ...frame } : { ...f }
    );
    const nextRootTransforms = originalRootTransformsRef.current.map((rt, i) =>
      i === displayFrame
        ? { pos: [...root.pos] as [number, number, number], quat: [...root.quat] as [number, number, number, number] }
        : { pos: [...rt.pos] as [number, number, number], quat: [...rt.quat] as [number, number, number, number] }
    );

    if (!originalSimulationData) {
      toast('warning', '原始仿真数据尚未加载完成');
      return;
    }

    const payload = buildSimulationPayloadFromFrames(originalSimulationData, nextFrames, jointKeyToUrdfName, nextRootTransforms);

    // 后端模式：上传
    const userId = api.getStoredUserId();
    const simulationId = taskDetail?.simulation_id;
    if (!simulationId) {
      toast('warning', '缺少 simulation_id');
      return;
    }
    try {
      await api.post(`/api/v1/simulation/${simulationId}/upload`, payload, {
        headers: { 'x-user-id': userId },
      });
    } catch (error) {
      console.error('Save current frame failed:', error);
      toast('error', '保存失败，请稍后重试');
      return;
    }

    // 保存成功后只提交当前帧到底层 simulationFrames
    setSimulationFrames(prev => {
      if (displayFrame < 0 || displayFrame >= prev.length) return prev;
      const next = [...prev];
      next[displayFrame] = { ...frame };
      return next;
    });

    // 只更新当前帧的保存基准（joint + root）
    setOriginalSimulationData(payload);
    originalFramesRef.current[displayFrame] = {
      ...frame,
    };
    originalRootTransformsRef.current[displayFrame] = {
      pos: [...root.pos] as [number, number, number],
      quat: [...root.quat] as [number, number, number, number],
    };
    bumpOriginalFramesVersion();

    // 重新判断是否还有其它未保存帧——用当前实际数据而非 nextFrames
    setUnsaved(hasUnsavedChanges(displayFrames, rootTransforms));
    // 保存当前帧不清空撤销栈，保留操作历史
    jointDragActiveRef.current = false;
    baseEditActiveRef.current = false;

    toast('success', `已保存第 ${currentFrame + 1} 帧的修改`);
  };
  // Selected frame within interval (for setting keyframe)
  const [selectedFrameInInterval, setSelectedFrameInInterval] = useState<number | null>(null);
  // Hovered frame on timeline (for showing set keyframe button)
  const [hoveredFrame, setHoveredFrame] = useState<number | null>(null);



  // Interpolation
  const [showInterpolateModal, setShowInterpolateModal] = useState(false);
  const [selectedJointsForInterpolation, setSelectedJointsForInterpolation] = useState<JointKey[]>(JOINT_DEFS.map(d => d.key));
  // 当模型关节映射变化时，自动裁剪已选关节列表（移除不存在的关节）
  useEffect(() => {
    const activeKeys = new Set(activeJointDefs.map(d => d.key));
    setSelectedJointsForInterpolation(prev => prev.filter(k => activeKeys.has(k)));
  }, [activeJointDefs]);
  // Python 插值选项
  const [pythonInterpOptions, setPythonInterpOptions] = useState({
    interpBasePos: false,
    interpBaseHeight: false,
    interpBaseQuat: false,
    interpJoints: true,
    interpFeetLevel: false,
  });


  useEffect(() => {
    const abortController = new AbortController();
    let cancelled = false;

    const loadTaskDetail = async () => {
      if (!id) return;
      // 切换任务前清空上一个任务的区间数据
      clearKeyframeIntervals();
      setSelectedIntervalId(null);
      setKeyframes([]);
      setPersistedKeyframeFrames(new Set());
      setLoadingTask(true);
      try {
        const detail = await api.task.detail(id, { origin_type: 'user' }, { headers: { language: 'zh' } });
        if (cancelled) return;

        setTaskDetail(detail);

        // 加载仿真数据
        const userId = api.getStoredUserId();
        const simulationId = detail?.simulation_id;

        if (!simulationId) {
          toast('warning', '缺少 simulation_id');
          setSimulationFrames(EMPTY_SIMULATION_FRAMES);
          return;
        }

        const {
          rawData,
          frames,
          rootTransforms: rt,
          jointKeyToUrdfName: dynamicMap,
          csvFieldNames: remoteFieldNames,
        } = await loadSimulationCsv(
          simulationId,
          userId,
          abortController.signal,
        );

        // 从仿真接口获取播放总时长
        try {
          const durRes = await api.simulation.getDuration(simulationId);
          danceDurations = durRes.duration;
        } catch (durErr) {
          console.warn('获取 duration 失败，回退到任务详情中的时长:', durErr);
          danceDurations = detail.duration ?? 0;
        }

        if (cancelled) return;

        if (!frames || frames.length === 0) {
          setSimulationFrames(EMPTY_SIMULATION_FRAMES);
          setOriginalSimulationData(null);
          setJointKeyToUrdfName({});
          setRootTransforms([]);
          toast('error', '仿真数据为空或解析失败');
          return;
        }

        setOriginalSimulationData(rawData);
        setSimulationFrames(frames);
        originalFramesRef.current = frames.map(f => ({ ...f })); // 深拷贝原始数据
        originalRootTransformsRef.current = rt.map(r => ({ pos: [...r.pos] as [number, number, number], quat: [...r.quat] as [number, number, number, number] }));
        bumpOriginalFramesVersion();
        csvOriginalFramesRef.current = frames.map(f => ({ ...f }));
        setJointKeyToUrdfName(dynamicMap);
        setCsvFieldNames(remoteFieldNames);
        setRootTransforms(rt);
        csvOriginalRootTransformsRef.current = rt;
        csvOriginalSimulationDataRef.current = rawData;
        setCurrentFrame(0);
        toast('success', `已加载 ${frames.length} 帧仿真数据`);

        // 加载服务端已编辑帧集合，用于高亮历史编辑区间
        try {
          const editSetRes = await api.simulation.getEditSet(simulationId);
          console.log('[edit-set] 接口返回:', editSetRes);
          if (!cancelled && editSetRes?.edit_set) {
            console.log('[edit-set] 已编辑帧序号:', editSetRes.edit_set);
            setEditedFrameSet(new Set(editSetRes.edit_set));
          }
        } catch (editErr) {
          // edit-set 加载失败不影响主流程，仅静默忽略
          console.warn('加载 edit-set 失败:', editErr);
        }
      } catch (error) {
        if (cancelled || (error instanceof DOMException && error.name === 'AbortError')) {
          return; // 请求被取消，不显示错误
        }
        console.error('Load task detail failed:', error);
        toast('error', '加载任务详情失败');
        setSimulationFrames(EMPTY_SIMULATION_FRAMES);
      } finally {
        if (!cancelled) {
          setLoadingTask(false);
        }
      }
    };

    loadTaskDetail();

    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [id]);


  const [isInterpolating, setIsInterpolating] = useState(false);

  // 所有 Python 操作互斥：任一操作进行中时，禁用其他 Python 按钮
  const isPythonBusy = isInterpolating || !!isAnkleLockedUprighting || isSmartFootAdjusting || isFootLeveling || isRestoringBackup;

  // --- Computed ---
  const selectedInterval = keyframeIntervals.find(i => i.id === selectedIntervalId) ?? null;
  // const currentFrameKeyframe = keyframes.find(k => k.frame === currentFrame);
  const simulationFrameCount = simulationFrames.length > 0 ? simulationFrames.length : TOTAL_FRAMES;
  const timelineFrameCount = selectedInterval ? (selectedInterval.endFrame - selectedInterval.startFrame + 1) : simulationFrameCount;
  const playbackMaxFrame = Math.max(0, simulationFrameCount - 1);
  const ppf = BASE_PPF * zoom; // pixels per frame

  // Frame ↔ pixel helpers
  const f2x = useCallback((f: number) => f * ppf, [ppf]);
  const x2f = useCallback((x: number) => Math.max(0, Math.min(playbackMaxFrame, Math.floor(x / ppf))), [ppf, playbackMaxFrame]);

  // 逐帧更新 DOM（不触发 React 重渲染），用于播放时平滑更新控件
  const updatePlaybackDom = useCallback((frame: number) => {
    const safeFrame = Math.max(0, Math.min(frame, playbackMaxFrame));
    if (playbackSliderRef.current) {
      playbackSliderRef.current.value = String(safeFrame + 1);
    }
    if (playbackFrameInputRef.current) {
      playbackFrameInputRef.current.value = String(safeFrame + 1);
    }
    if (playbackTimeLabelRef.current) {
      playbackTimeLabelRef.current.textContent = `${(safeFrame / FPS).toFixed(2)}s`;
    }
    if (playbackHeadRef.current) {
      playbackHeadRef.current.style.transform = `translateX(${f2x(safeFrame)}px)`;
    }
    if (playbackHeadLabelRef.current) {
      playbackHeadLabelRef.current.textContent = String(safeFrame + 1);
    }
  }, [playbackMaxFrame, f2x]);

  // 播放逐帧回调：同时更新 currentFrameRef 和 DOM
  const handlePlaybackFrame = useCallback((frame: number) => {
    if (pausingSyncRef.current) return;
    const safeFrame = Math.max(0, Math.min(frame, playbackMaxFrame));
    currentFrameRef.current = safeFrame;
    updatePlaybackDom(safeFrame);
  }, [playbackMaxFrame, updatePlaybackDom]);

  // 统一跳帧：同时更新 ref、DOM 和 React state
  const setFrameImmediate = useCallback((frame: number) => {
    const safeFrame = Math.max(0, Math.min(frame, playbackMaxFrame));
    currentFrameRef.current = safeFrame;
    updatePlaybackDom(safeFrame);
    setCurrentFrame(safeFrame);
  }, [playbackMaxFrame, updatePlaybackDom]);

  // 非播放状态同步 DOM
  useEffect(() => {
    if (!playing) {
      updatePlaybackDom(currentFrame);
    }
  }, [currentFrame, playing, updatePlaybackDom]);

  /**
   * 统一更新处理区间与时间轴选择区间。
   * 内部使用 0-based，界面输入与显示使用 1-based。
   * 任何入口（输入框、拖拽、区间显示输入框）都必须调用这里，
   * 避免 processingRange 和 dragSelection 各自更新后出现不同步。
   */
  const syncProcessingRange = useCallback((start: number, end: number) => {
    if (!Number.isFinite(start) || !Number.isFinite(end)) return;

    const clampedStart = Math.max(0, Math.min(Math.trunc(start), playbackMaxFrame));
    const clampedEnd = Math.max(0, Math.min(Math.trunc(end), playbackMaxFrame));
    const next = {
      start: Math.min(clampedStart, clampedEnd),
      end: Math.max(clampedStart, clampedEnd),
    };

    setProcessingRange(next);
    setDragSelection(next);
  }, [playbackMaxFrame]);

  // Get joints for a specific frame

  const getJointsAtFrame = useCallback((frame: number): JointAngles => {
    // 首先检查是否在关键帧区间内
    const interval = keyframeIntervals.find(i => frame >= i.startFrame && frame <= i.endFrame);
    if (interval) {
      const idx = frame - interval.startFrame;
      if (idx >= 0 && idx < interval.frames.length) {
        return interval.frames[idx];
      }
    }

    // 回退到仿真帧
    if (frame >= 0 && frame < simulationFrames.length) {
      return simulationFrames[frame];
    }

    return emptyJoints();
  }, [keyframeIntervals, simulationFrames]);



  const currentJoints = useMemo(() => getJointsAtFrame(displayFrame), [getJointsAtFrame, displayFrame]);
  /** 区间覆盖帧 Map（frameIndex → JointAngles），用于增量合并到 simulationFrames */
  const intervalOverrides = useMemo(() => {
    const m = new Map<number, JointAngles>();
    for (const interval of keyframeIntervals) {
      for (let i = 0; i < interval.frames.length; i++) {
        m.set(interval.startFrame + i, interval.frames[i]);
      }
    }
    return m;
  }, [keyframeIntervals]);
  /** 合并了区间编辑的帧数组——仅覆盖被编辑的帧，不做全量拷贝 */
  const displayFrames = useMemo(() => {
    if (intervalOverrides.size === 0) return simulationFrames;
    const result = [...simulationFrames];
    for (const [idx, joints] of intervalOverrides) {
      if (idx >= 0 && idx < result.length) result[idx] = joints;
    }
    return result;
  }, [simulationFrames, intervalOverrides]);
  /** 检测哪些帧有未保存的修改（与保存基准比较 joint frames + rootTransforms，不限区间） */
  const unsavedFrameIndices = useMemo(() => {
    const set = new Set<number>();
    const orig = originalFramesRef.current;
    const origRt = originalRootTransformsRef.current;
    if (orig.length === 0) return set;
    for (let i = 0; i < displayFrames.length && i < orig.length; i++) {
      const cur = displayFrames[i];
      const origFrame = orig[i];
      let diff = false;
      for (const key of Object.keys(cur) as JointKey[]) {
        if (Math.abs((cur[key] ?? 0) - (origFrame[key] ?? 0)) > 1e-6) {
          diff = true;
          break;
        }
      }
      // 也比较 rootTransforms
      if (!diff && i < rootTransforms.length && i < origRt.length) {
        const curRt = rootTransforms[i];
        const origR = origRt[i];
        if (curRt.pos.some((v, j) => Math.abs(v - origR.pos[j]) > 1e-6) ||
            curRt.quat.some((v, j) => Math.abs(v - origR.quat[j]) > 1e-6)) {
          diff = true;
        }
      }
      if (diff) set.add(i);
    }
    return set;
  }, [displayFrames, originalFramesVersion, rootTransforms]);
  /** 将已编辑帧序号集合合并为连续区间，用于时间轴上高效渲染 */
  const editedRanges = useMemo(() => {
    const ranges: { start: number; end: number }[] = [];
    if (editedFrameSet.size === 0) return ranges;
    const sorted = Array.from(editedFrameSet).sort((a, b) => a - b);
    let rangeStart = sorted[0];
    let rangeEnd = sorted[0];
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] === rangeEnd + 1) {
        rangeEnd = sorted[i];
      } else {
        ranges.push({ start: rangeStart, end: rangeEnd });
        rangeStart = sorted[i];
        rangeEnd = sorted[i];
      }
    }
    ranges.push({ start: rangeStart, end: rangeEnd });
    return ranges;
  }, [editedFrameSet]);
  /** 当前帧中哪些关节有未保存的修改（JointKey set，不限区间） */
  const unsavedJointKeys = useMemo(() => {
    const set = new Set<JointKey>();
    // 用 displayFrames（含区间编辑覆盖）与 originalFramesRef（CSV原始值）比较
    const orig = originalFramesRef.current;
    if (displayFrame < 0 || displayFrame >= orig.length) return set;
    const cur = displayFrames[displayFrame];
    const origFrame = orig[displayFrame];
    if (!cur || !origFrame) return set;
    for (const key of Object.keys(cur) as JointKey[]) {
      if (Math.abs((cur[key] ?? 0) - (origFrame[key] ?? 0)) > 1e-6) {
        set.add(key);
      }
    }
    return set;
  }, [displayFrame, displayFrames, originalFramesVersion]);
  /** 当前帧的浮动基座变换 */
  const currentRootTransform = useMemo<RootTransform | null>(
    () => (displayFrame >= 0 && displayFrame < rootTransforms.length ? rootTransforms[displayFrame] : null),
    [displayFrame, rootTransforms],
  );

  /** 当前机型是否支持 MuJoCo 物理渲染（有 mujocoModelDir 配置） */
  const supportsMujoco = useMemo(() => {
    const rm = taskDetail?.robot_model;
    if (!rm) return false;
    return !!findModelConfigByRobotModel(rm)?.mujocoModelDir;
  }, [taskDetail?.robot_model]);

  /** 当前机型的 modelId（配置 ID 格式，如 "unitree-g1"），用于纯 URDF 渲染回退 */
  const renderModelId = useMemo(() => {
    const rm = taskDetail?.robot_model;
    if (!rm) return DEFAULT_ROBOT_MODEL_ID;
    return findModelConfigByRobotModel(rm)?.id ?? DEFAULT_ROBOT_MODEL_ID;
  }, [taskDetail?.robot_model]);

  // Base Control 修改 → 写回 rootTransforms
  const handleBaseTransformChange = useCallback((frame: number, rt: RootTransform) => {
    if (frame < 0 || frame >= rootTransforms.length || frame >= displayFrames.length) return;

    const nextRoots = [...rootTransforms];
    nextRoots[frame] = {
      pos: [...rt.pos] as [number, number, number],
      quat: [...rt.quat] as [number, number, number, number],
    };
    setRootTransforms(nextRoots);
    setUnsaved(hasUnsavedChanges(displayFrames, nextRoots));
    setMjHandle(prev => prev ? { ...prev } : null);
    setTick(t => t + 1);
  }, [rootTransforms, displayFrames, hasUnsavedChanges]);

  // Base Control 复位回调 — 使用 CSV 原始值作为复位目标
  const handleBaseResetPos = useCallback(() => {
    const mj = mjHandleRef.current;
    if (!mj) return;
    const originalRt = csvOriginalRootTransformsRef.current[displayFrame];
    if (!originalRt) return;
    pushUndoSnapshot();
    const curRt = rootTransforms[displayFrame];
    // 复位位置：只恢复 pos，保留当前 quat
    const newRt: RootTransform = {
      pos: [...originalRt.pos] as [number, number, number],
      quat: curRt ? [...curRt.quat] as [number, number, number, number] : [...originalRt.quat] as [number, number, number, number],
    };
    writeMjBaseQpos(mj, newRt);
    const nextRoots = [...rootTransforms];
    nextRoots[displayFrame] = newRt;
    setRootTransforms(nextRoots);
    setUnsaved(hasUnsavedChanges(displayFrames, nextRoots));
    setMjHandle(prev => prev ? { ...prev } : null);
    setTick(t => t + 1);
    setBaseSyncTick(t => t + 1);
  }, [displayFrame, rootTransforms, displayFrames, writeMjBaseQpos, pushUndoSnapshot, hasUnsavedChanges]);

  const handleBaseResetRot = useCallback(() => {
    const mj = mjHandleRef.current;
    if (!mj) return;
    const originalRt = csvOriginalRootTransformsRef.current[displayFrame];
    if (!originalRt) return;
    pushUndoSnapshot();
    const curRt = rootTransforms[displayFrame];
    // 复位旋转：只恢复 quat，保留当前 pos
    const newRt: RootTransform = {
      pos: curRt ? [...curRt.pos] as [number, number, number] : [...originalRt.pos] as [number, number, number],
      quat: [...originalRt.quat] as [number, number, number, number],
    };
    writeMjBaseQpos(mj, newRt);
    const nextRoots = [...rootTransforms];
    nextRoots[displayFrame] = newRt;
    setRootTransforms(nextRoots);
    setUnsaved(hasUnsavedChanges(displayFrames, nextRoots));
    setMjHandle(prev => prev ? { ...prev } : null);
    setTick(t => t + 1);
    setBaseSyncTick(t => t + 1);
  }, [displayFrame, rootTransforms, displayFrames, writeMjBaseQpos, pushUndoSnapshot, hasUnsavedChanges]);

  const handleBaseResetAll = useCallback(() => {
    const mj = mjHandleRef.current;
    if (!mj) return;
    const originalRt = csvOriginalRootTransformsRef.current[displayFrame];
    if (!originalRt) return;
    pushUndoSnapshot();
    // 复位全部：恢复 pos + quat
    const newRt: RootTransform = {
      pos: [...originalRt.pos] as [number, number, number],
      quat: [...originalRt.quat] as [number, number, number, number],
    };
    writeMjBaseQpos(mj, newRt);
    const nextRoots = [...rootTransforms];
    nextRoots[displayFrame] = newRt;
    setRootTransforms(nextRoots);
    setUnsaved(hasUnsavedChanges(displayFrames, nextRoots));
    setMjHandle(prev => prev ? { ...prev } : null);
    setTick(t => t + 1);
    setBaseSyncTick(t => t + 1);
  }, [displayFrame, rootTransforms, displayFrames, writeMjBaseQpos, pushUndoSnapshot, hasUnsavedChanges]);

  // --- Playback ---
  // 播放逻辑：帧推进已移入 MujocoViewer 的 useFrame 内部（绕过 React 状态更新实现流畅播放）。
  // 此处仅负责：播放开始时将帧号调整到合法范围内。
  useEffect(() => {
    if (!playing) return;

    const startFrame = selectedInterval && !playAll ? selectedInterval.startFrame : 0;
    const endFrame = selectedInterval && !playAll ? selectedInterval.endFrame : playbackMaxFrame;

    const frame = currentFrameRef.current;
    if (frame < startFrame || frame > endFrame) {
      setFrameImmediate(startFrame);
    }
  }, [playing, playAll, selectedInterval, playbackMaxFrame]);

  // 初始化/重置 processingRange 为全帧范围
  useEffect(() => {
    if (simulationFrameCount > 0) {
      setProcessingRange({ start: 0, end: Math.max(0, simulationFrameCount - 1) });
    }
  }, [simulationFrameCount]);

  // --- Handlers ---

  // Set as keyframe interval
  const setAsKeyframe = useCallback(() => {
    if (!dragSelection) return;
    const start = Math.min(dragSelection.start, dragSelection.end);
    const end = Math.max(dragSelection.start, dragSelection.end);
    if (end - start < 2) { toast('warning', '区间至少需要3帧'); return; }

    pushUndoSnapshot();

    // 如果已存在插帧区间，先删除旧的（限制只能有一个）
    if (keyframeIntervals.length > 0) {
      keyframeIntervals.forEach(i => deleteKeyframeInterval(i.id));
      toast('info', '已自动替换旧的插帧区间');
    }

    const name = `区间1`;
    const color = INTERVAL_COLORS[0];
    const frames = Array.from({ length: end - start + 1 }, (_, i) => {
      const simIdx = start + i;
      const frameOverride = intervalOverrides.get(simIdx);
      if (frameOverride) {
        return { ...frameOverride };
      }
      if (simIdx >= 0 && simIdx < simulationFrames.length) {
        return { ...simulationFrames[simIdx] };
      }
      return { ...emptyJoints() };
    });

    const interval: KeyframeInterval = {
      id: `ki_${Date.now()}`,
      name,
      startFrame: start,
      endFrame: end,
      frames,
      color,
    };

    addKeyframeInterval(interval);
    setSelectedIntervalId(interval.id);
    setFrameImmediate(start);
    setDragSelection(null);
    setUnsaved(true);
    toast('success', `已创建「${name}」（帧 ${start + 1}~${end + 1}，共 ${end - start + 1} 帧）`);
  }, [dragSelection, keyframeIntervals, addKeyframeInterval, deleteKeyframeInterval, setSelectedIntervalId, simulationFrames, intervalOverrides, pushUndoSnapshot]);

  // Reset current frame's all joints back to original CSV values
  const resetCurrentFrame = useCallback(() => {
    const orig = csvOriginalFramesRef.current;
    if (displayFrame < 0 || displayFrame >= orig.length) return;
    pushUndoSnapshot();

    // 重置后的帧数据（用于 dirty 检测）
    const origRt = csvOriginalRootTransformsRef.current;
    const resetFrame = { ...orig[displayFrame] };
    const resetRoot = displayFrame < origRt.length
      ? { pos: [...origRt[displayFrame].pos] as [number, number, number], quat: [...origRt[displayFrame].quat] as [number, number, number, number] }
      : null;

    if (selectedInterval) {
      const interval = keyframeIntervals.find(i => i.id === selectedInterval.id)!;
      const frames = interval.frames.map((f, idx) => {
        const simIdx = interval.startFrame + idx;
        if (simIdx === displayFrame && simIdx < orig.length) {
          return { ...orig[simIdx] };
        }
        return { ...f };
      });
      updateKeyframeInterval(interval.id, { frames });
    } else {
      setSimulationFrames(prev => {
        const newFrames = [...prev];
        newFrames[displayFrame] = resetFrame;
        return newFrames;
      });
    }

    // Update MuJoCo joints
    const mj = mjHandleRef.current;
    if (mj && jointKeyToUrdfName) {
      const origFrame = orig[displayFrame];
      for (const [key, urdfName] of Object.entries(jointKeyToUrdfName)) {
        const addr = mj.qmap[urdfName];
        if (addr !== undefined && origFrame) {
          mj.data.qpos[addr] = (origFrame[key as JointKey] ?? 0) * Math.PI / 180;
        }
      }
      mj.M.mj_forward(mj.model, mj.data);
      setMjHandle(prev => prev ? { ...prev } : null);
    }

    // Reset rootTransform for this frame（不修改保存基准）
    if (resetRoot) {
      setRootTransforms(prev => {
        const next = [...prev];
        if (displayFrame < next.length) {
          next[displayFrame] = resetRoot;
        }
        return next;
      });
      // Reset MuJoCo base qpos too
      const mj2 = mjHandleRef.current;
      if (mj2) {
        writeMjBaseQpos(mj2, resetRoot);
        setMjHandle(prev => prev ? { ...prev } : null);
      }
    }

    // 重置只修改当前数据，不修改保存基准
    // dirty 状态比较重置后的数据 vs 保存基准
    const nextDisplayFrames = displayFrames.map((f, i) =>
      i === displayFrame ? resetFrame : { ...f }
    );
    const nextRootTransforms = rootTransforms.map((rt, i) =>
      i === displayFrame && resetRoot ? resetRoot : rt
    );
    setUnsaved(hasUnsavedChanges(nextDisplayFrames, nextRootTransforms));

    toast('success', `已重置第 ${displayFrame + 1} 帧所有关节到 CSV 原始值`);
  }, [displayFrame, selectedInterval, keyframeIntervals, updateKeyframeInterval, setSimulationFrames, jointKeyToUrdfName, displayFrames, rootTransforms, writeMjBaseQpos, hasUnsavedChanges, pushUndoSnapshot]);

  // Reset all frames back to original CSV values
  const resetAllFrames = useCallback(() => {
    const orig = csvOriginalFramesRef.current;
    if (orig.length === 0) return;
    pushUndoSnapshot();

    // 1. Reset simulationFrames (all frames, not just intervals)
    const resetFrames = orig.map(f => ({ ...f }));
    setSimulationFrames(resetFrames);

    // 2. Reset all interval frames to CSV original values
    keyframeIntervals.forEach(interval => {
      const frames = Array.from({ length: interval.endFrame - interval.startFrame + 1 }, (_, i) => {
        const simIdx = interval.startFrame + i;
        if (simIdx >= 0 && simIdx < orig.length) {
          return { ...orig[simIdx] };
        }
        return { ...emptyJoints() };
      });
      updateKeyframeInterval(interval.id, { frames });
    });

    // 3. Reset rootTransforms to CSV original（不修改保存基准）
    const origRt = csvOriginalRootTransformsRef.current;
    const resetRoots = origRt.length > 0
      ? origRt.map(r => ({ pos: [...r.pos] as [number, number, number], quat: [...r.quat] as [number, number, number, number] }))
      : rootTransforms.map(r => ({ pos: [...r.pos] as [number, number, number], quat: [...r.quat] as [number, number, number, number] }));
    setRootTransforms(resetRoots);

    // 重置只修改当前数据，不修改保存基准
    // dirty 状态比较重置后的数据 vs 保存基准
    setUnsaved(hasUnsavedChanges(resetFrames, resetRoots));

    toast('success', '已恢复所有帧到 CSV 原始值');
  }, [keyframeIntervals, updateKeyframeInterval, setSimulationFrames, rootTransforms, hasUnsavedChanges, pushUndoSnapshot]);

  // 一键还原初始帧 — 从服务端拉取备份数据，替换本地编辑状态并持久化到服务端
  const handleRestoreBackup = useCallback(async () => {
    const simulationId = taskDetail?.simulation_id;
    if (!simulationId) {
      toast('warning', '缺少 simulation_id');
      return;
    }
    setIsRestoringBackup(true);
    try {
      const userId = api.getStoredUserId();
      const { rawData, frames, rootTransforms: rt, jointKeyToUrdfName: dynamicMap, csvFieldNames: remoteFieldNames } =
        await loadSimulationCsv(simulationId, userId, undefined, 'backup');

      if (!frames || frames.length === 0) {
        toast('error', '备份数据为空或解析失败');
        return;
      }

      // 1. 构建备份数据的 payload 并上传到服务端（持久化还原）
      const payload = buildSimulationPayloadFromFrames(rawData, frames, dynamicMap, rt);
      await api.post(`/api/v1/simulation/${simulationId}/upload`, payload, {
        headers: { 'x-user-id': userId },
      });

      pushUndoSnapshot();

      // 2. 替换 simulationFrames 和 rootTransforms
      setSimulationFrames(frames);
      setRootTransforms(rt);

      // 3. 清空所有帧区间、关键帧标记、编辑历史（还原到未编辑状态）
      clearKeyframeIntervals();
      setKeyframes([]);
      setEditedFrameSet(new Set());
      setPersistedKeyframeFrames(new Set());
      setBaseline(null);

      // 4. 更新所有原始数据引用（备份数据即为新的基准）
      setOriginalSimulationData(payload);
      originalFramesRef.current = frames.map(f => ({ ...f }));
      originalRootTransformsRef.current = rt.map(r => ({ pos: [...r.pos] as [number, number, number], quat: [...r.quat] as [number, number, number, number] }));
      csvOriginalFramesRef.current = frames.map(f => ({ ...f }));
      csvOriginalRootTransformsRef.current = rt;
      csvOriginalSimulationDataRef.current = rawData;
      bumpOriginalFramesVersion();

      // 5. 更新关节映射和CSV字段名
      setJointKeyToUrdfName(dynamicMap);
      setCsvFieldNames(remoteFieldNames);

      // 6. 清空撤销栈
      undoStackRef.current = [];
      jointDragActiveRef.current = false;
      baseEditActiveRef.current = false;

      setUnsaved(false);
      toast('success', `已还原到初始备份数据（${frames.length} 帧）`);
    } catch (error) {
      console.error('还原备份数据失败:', error);
      toast('error', '还原备份数据失败，请稍后重试');
    } finally {
      setIsRestoringBackup(false);
    }
  }, [taskDetail?.simulation_id, pushUndoSnapshot, clearKeyframeIntervals, setKeyframes, setEditedFrameSet, setSimulationFrames, setRootTransforms, setOriginalSimulationData, setJointKeyToUrdfName, setCsvFieldNames, hasUnsavedChanges, bumpOriginalFramesVersion]);

  // Joint change handler — edits current frame within interval
  const chJoint = useCallback((key: JointKey, newVal: number) => {
    // Push undo snapshot on the first change of a drag session
    if (!jointDragActiveRef.current) {
      jointDragActiveRef.current = true;
      pushUndoSnapshot();
    }

    const def = JOINT_DEFS.find(j => j.key === key)!;
    const clamped = Math.round(Math.max(def.min, Math.min(def.max, newVal)) * 100) / 100;

    // 绕过 React 批处理：直接写 MuJoCo qpos 实现丝滑拖拽
    const mj = mjHandleRef.current;
    if (mj && jointKeyToUrdfName) {
      const urdfName = jointKeyToUrdfName[key];
      if (urdfName) {
        let addr = mj.qmap[urdfName];
        if (addr !== undefined) {
          mj.data.qpos[addr] = clamped * Math.PI / 180;
          mj.M.mj_forward(mj.model, mj.data);
        }
      }
    }

    if (selectedInterval) {
      // 关键帧区间模式：修改当前帧在区间内的值
      const interval = keyframeIntervals.find(i => i.id === selectedInterval.id)!;
      const frameIdx = displayFrame - interval.startFrame;
      if (frameIdx >= 0 && frameIdx < interval.frames.length) {
        const frames = interval.frames.map(f => ({ ...f }));
        frames[frameIdx][key] = clamped;
        updateKeyframeInterval(interval.id, { frames });
      } else {
        // displayFrame 在选中区间之外——回退到直接修改仿真帧
        if (displayFrame >= 0 && displayFrame < simulationFrames.length) {
          const newFrames = [...simulationFrames];
          newFrames[displayFrame] = { ...newFrames[displayFrame], [key]: clamped };
          setSimulationFrames(newFrames);
        }
      }
    } else {
      // 无区间模式：直接修改仿真帧
      if (displayFrame >= 0 && displayFrame < simulationFrames.length) {
        const newFrames = [...simulationFrames];
        newFrames[displayFrame] = { ...newFrames[displayFrame], [key]: clamped };
        setSimulationFrames(newFrames);
      }
    }
    setUnsaved(true);
  }, [selectedInterval, keyframeIntervals, displayFrame, updateKeyframeInterval, simulationFrames, jointKeyToUrdfName, pushUndoSnapshot]);

  const resetJoint = useCallback((key: JointKey) => {
    // 先校验，再创建撤销快照
    if (displayFrame < 0 || displayFrame >= csvOriginalFramesRef.current.length) return;
    if (selectedInterval && !keyframeIntervals.some(i => i.id === selectedInterval.id)) return;

    pushUndoSnapshot();
    const orig = csvOriginalFramesRef.current;
    const origVal = orig[displayFrame]?.[key] ?? 0;

    if (selectedInterval) {
      const interval = keyframeIntervals.find(i => i.id === selectedInterval.id);
      if (!interval) return;
      const frameIdx = displayFrame - interval.startFrame;
      const frames = interval.frames.map((f, idx) =>
        idx === frameIdx ? { ...f, [key]: origVal } : { ...f }
      );
      updateKeyframeInterval(interval.id, { frames });
    } else {
      if (displayFrame >= 0 && displayFrame < simulationFrames.length) {
        const newFrames = [...simulationFrames];
        newFrames[displayFrame] = { ...newFrames[displayFrame], [key]: origVal };
        setSimulationFrames(newFrames);
      }
    }
    // 直接更新 MuJoCo
    const mj = mjHandleRef.current;
    if (mj && jointKeyToUrdfName) {
      const urdfName = jointKeyToUrdfName[key];
      if (urdfName && mj.qmap[urdfName] !== undefined) {
        mj.data.qpos[mj.qmap[urdfName]] = origVal * Math.PI / 180;
        mj.M.mj_forward(mj.model, mj.data);
      }
    }

    // 重新计算 dirty 状态：与保存基准比较
    // 构造重置后的帧数据用于检测
    const nextDisplayFrames = displayFrames.map((f, i) =>
      i === displayFrame ? { ...f, [key]: origVal } : { ...f }
    );
    setUnsaved(hasUnsavedChanges(nextDisplayFrames, rootTransforms));

    toast('success', `${JOINT_DEFS.find(j => j.key === key)?.label} 已复位`);
  }, [selectedInterval, keyframeIntervals, displayFrame, updateKeyframeInterval, simulationFrames, jointKeyToUrdfName, pushUndoSnapshot, displayFrames, rootTransforms, hasUnsavedChanges]);


  // Python 插值逻辑
  const doPythonInterpolation = useCallback(async () => {
    if (!selectedInterval) return;

    // 检测区间内是否有未保存修改的关键帧
    const unsavedKfFrames = keyframes
      .filter(k => k.frame >= selectedInterval.startFrame && k.frame <= selectedInterval.endFrame)
      .filter(k => unsavedFrameIndices.has(k.frame));
    if (unsavedKfFrames.length > 0) {
      const frameList = unsavedKfFrames.map(k => k.frame + 1).join(', ');
      toast('warning', `请先保存修改的关键帧（帧 ${frameList}），或重置到该帧初始状态`);
      return;
    }

    setIsInterpolating(true);
    try {
      if (!originalSimulationData) {
        toast('warning', '原始仿真数据尚未加载完成');
        return;
      }

      const payload = buildSimulationPayloadFromFrames(
        originalSimulationData,
        displayFrames,
        jointKeyToUrdfName,
        rootTransforms,
      );

      // const robotModel = taskDetail?.robot_model || 'booster_k1';
      const allKeyFrames = [
        ...(keyframes
          .filter(k => k.frame >= selectedInterval.startFrame && k.frame <= selectedInterval.endFrame)
          .map(k => k.frame)
          .includes(selectedInterval.startFrame) ? [] : [selectedInterval.startFrame]),
        ...keyframes
          .filter(k => k.frame > selectedInterval.startFrame && k.frame < selectedInterval.endFrame)
          .map(k => k.frame),
        ...(keyframes
          .filter(k => k.frame >= selectedInterval.startFrame && k.frame <= selectedInterval.endFrame)
          .map(k => k.frame)
          .includes(selectedInterval.endFrame) ? [] : [selectedInterval.endFrame]),
      ].sort((a, b) => a - b);

      const mj = mjHandleRef.current;
      let newRawData: SimulationJsonResponse | null = null;
      if (mj) {
        try {
          newRawData = runLocalTrajectoryOp(mj, payload, (editor) => {
            editor.interpolation({
              startFrame: selectedInterval.startFrame,
              endFrame: selectedInterval.endFrame,
              interpBasePos: pythonInterpOptions.interpBasePos,
              interpBaseHeight: pythonInterpOptions.interpBaseHeight,
              interpBaseQuat: pythonInterpOptions.interpBaseQuat,
              interpJoints: pythonInterpOptions.interpJoints,
              interpFeetLevel: pythonInterpOptions.interpFeetLevel,
              keyframes1based: allKeyFrames.map(f => f + 1),
              selectedJointIndices: pythonInterpOptions.interpJoints
                ? selectedJointsForInterpolation
                    .map(key => jointKeyToCsvColIdx[key])
                    .filter((idx): idx is number => idx !== undefined)
                : [],
            });
          });
        } catch (error) {
          console.error('[robotEdit] 本地插值计算失败，回退到后端:', error);
          newRawData = null;
        }
      }
      // 已切换为纯本地计算，后端插值接口注释保留备用
      // if (newRawData == null) {
      //   const response = await api.post<{ data: SimulationJsonResponse }>(
      //     '/api/v1/edit/interpolate-file',
      //     {
      //       trajectory_data: payload,
      //       start_frame: selectedInterval.startFrame,
      //       end_frame: selectedInterval.endFrame,
      //       robot_model: robotModel,
      //       interp_base_pos: pythonInterpOptions.interpBasePos,
      //       interp_base_height: pythonInterpOptions.interpBaseHeight,
      //       interp_base_quat: pythonInterpOptions.interpBaseQuat,
      //       interp_joints: pythonInterpOptions.interpJoints,
      //       interp_feet_level: pythonInterpOptions.interpFeetLevel,
      //       keyframes_1based: allKeyFrames.map(f => f + 1),
      //       selected_joint_indices: pythonInterpOptions.interpJoints
      //         ? selectedJointsForInterpolation
      //             .map(key => jointKeyToCsvColIdx[key])
      //             .filter((idx): idx is number => idx !== undefined)
      //         : [],
      //     },
      //   );
      //   newRawData = response.data;
      // }

      if (!newRawData || !Array.isArray(newRawData) || newRawData.length === 0) {
        toast('error', '本地计算未就绪（3D 模型未加载），请稍后重试');
        return;
      }

      const { rawData: parsedRawData, frames: newFrames, rootTransforms: newRt, jointKeyToUrdfName: newDynamicMap } = loadLocalSimulationCsv(
        newRawData,
        currentFieldNames,
      );
      pushUndoSnapshot();
      setOriginalSimulationData(parsedRawData);
      setSimulationFrames(newFrames);
      setRootTransforms(newRt);
      if (Object.keys(newDynamicMap).length > 0) {
        setJointKeyToUrdfName(newDynamicMap);
      }
      // 同步更新当前选中区间的 frames
      if (selectedInterval) {
        const patchedFrames = selectedInterval.frames.map((oldFrame, offset) => {
          const globalFrame = selectedInterval.startFrame + offset;
          if (newFrames[globalFrame]) {
            return { ...newFrames[globalFrame] };
          }
          return { ...oldFrame };
        });
        updateKeyframeInterval(selectedInterval.id, {
          frames: patchedFrames,
        });
      }
      setUnsaved(true);
      toast('success', `插值完成并回填`);
    } catch (error) {
      console.error('插值失败:', error);
      toast('error', error instanceof Error ? error.message : '插值失败');
    } finally {
      setIsInterpolating(false);
      setShowInterpolateModal(false);
    }
  }, [selectedInterval, simulationFrames, originalSimulationData, jointKeyToUrdfName, pythonInterpOptions, selectedJointsForInterpolation, jointKeyToCsvColIdx, displayFrames, rootTransforms, unsavedFrameIndices, keyframes, pushUndoSnapshot, updateKeyframeInterval]);

  // ===== 前后腰扶正 / 左右腰扶正（锁踝调基座） =====
  const handleAnkleLockedBaseUpright = useCallback(async (axis: 'pitch' | 'roll') => {
    if (!selectedInterval) {
      toast('warning', '请先选择关键帧区间');
      return;
    }
    if (processingRange.end <= processingRange.start) {
      toast('warning', '请先设置有效的帧区间');
      return;
    }

    setIsAnkleLockedUprighting(axis);
    try {
      if (!originalSimulationData) {
        toast('warning', '原始仿真数据尚未加载完成');
        return;
      }

      const payload = buildSimulationPayloadFromFrames(
        originalSimulationData,
        displayFrames,
        jointKeyToUrdfName,
        rootTransforms,
      );

      // const robotModel = taskDetail?.robot_model || 'booster_k1';

      const mj = mjHandleRef.current;
      let newRawData: SimulationJsonResponse | null = null;
      if (mj) {
        try {
          newRawData = runLocalTrajectoryOp(mj, payload, (editor) => {
            editor.ankleLockedBaseUpright(processingRange.start, processingRange.end, axis);
          });
        } catch (error) {
          console.error('[robotEdit] 本地锁踝调腰计算失败，回退到后端:', error);
          newRawData = null;
        }
      }
      // 已切换为纯本地计算，后端锁踝调腰接口注释保留备用
      // if (newRawData == null) {
      //   const result = await api.simulation.waistAdjust({
      //     trajectory_data: payload,
      //     start_frame: processingRange.start,
      //     end_frame: processingRange.end,
      //     robot_model: robotModel,
      //     axis,
      //   });
      //   newRawData = result.data as unknown as SimulationJsonResponse;
      // }
      if (newRawData == null) {
        toast('error', '本地计算未就绪（3D 模型未加载），请稍后重试');
        return;
      }

      const { rawData: parsedRawData, frames: newFrames, rootTransforms: newRt, jointKeyToUrdfName: newDynamicMap } = loadLocalSimulationCsv(
        newRawData,
        currentFieldNames,
      );
      pushUndoSnapshot();
      setOriginalSimulationData(parsedRawData);
      setSimulationFrames(newFrames);
      setRootTransforms(newRt);
      if (Object.keys(newDynamicMap).length > 0) {
        setJointKeyToUrdfName(newDynamicMap);
      }
      if (selectedInterval && processingRange.start >= selectedInterval.startFrame && processingRange.end <= selectedInterval.endFrame) {
        const patchedFrames = selectedInterval.frames.map((oldFrame, offset) => {
          const globalFrame = selectedInterval.startFrame + offset;
          if (globalFrame >= processingRange.start && globalFrame <= processingRange.end && newFrames[globalFrame]) {
            return { ...newFrames[globalFrame] };
          }
          return { ...oldFrame };
        });
        updateKeyframeInterval(selectedInterval.id, {
          frames: patchedFrames,
        });
      }
      setUnsaved(true);
      const label = axis === 'pitch' ? '前后腰扶正' : '左右腰扶正';
      toast('success', `${label}完成`);
    } catch (error) {
      const label = axis === 'pitch' ? '前后腰扶正' : '左右腰扶正';
      console.error(`${label}失败:`, error);
      toast('error', error instanceof Error ? error.message : `${label}失败`);
    } finally {
      setIsAnkleLockedUprighting(false);
    }
  }, [processingRange, selectedInterval, simulationFrames, originalSimulationData, jointKeyToUrdfName, displayFrames, rootTransforms, pushUndoSnapshot, updateKeyframeInterval]);

  // ===== 智能调脚 =====
  const handleSmartFootAdjust = useCallback(async () => {
    if (!selectedInterval) {
      toast('warning', '请先选择关键帧区间');
      return;
    }
    if (processingRange.end <= processingRange.start) {
      toast('warning', '请先设置有效的帧区间');
      return;
    }

    setIsSmartFootAdjusting(true);
    try {
      if (!originalSimulationData) {
        toast('warning', '原始仿真数据尚未加载完成');
        return;
      }

      const payload = buildSimulationPayloadFromFrames(
        originalSimulationData,
        displayFrames,
        jointKeyToUrdfName,
        rootTransforms,
      );

      // const robotModel = taskDetail?.robot_model || 'booster_k1';

      const mj = mjHandleRef.current;
      let newRawData: SimulationJsonResponse | null = null;
      if (mj) {
        try {
          newRawData = runLocalTrajectoryOp(mj, payload, (editor) => {
            editor.smartFootAdjust(processingRange.start, processingRange.end, 'auto');
          });
        } catch (error) {
          console.error('[robotEdit] 本地智能调脚计算失败，回退到后端:', error);
          newRawData = null;
        }
      }
      // 已切换为纯本地计算，后端智能调脚接口注释保留备用
      // if (newRawData == null) {
      //   const result = await api.simulation.smartFootAdjust({
      //     trajectory_data: payload,
      //     start_frame: processingRange.start,
      //     end_frame: processingRange.end,
      //     robot_model: robotModel,
      //     scope: 'auto',
      //   });
      //   newRawData = result.data as unknown as SimulationJsonResponse;
      // }
      if (newRawData == null) {
        toast('error', '本地计算未就绪（3D 模型未加载），请稍后重试');
        return;
      }

      const { rawData: parsedRawData, frames: newFrames, rootTransforms: newRt, jointKeyToUrdfName: newDynamicMap } = loadLocalSimulationCsv(
        newRawData,
        currentFieldNames,
      );
      pushUndoSnapshot();
      setOriginalSimulationData(parsedRawData);
      setSimulationFrames(newFrames);
      setRootTransforms(newRt);
      if (Object.keys(newDynamicMap).length > 0) {
        setJointKeyToUrdfName(newDynamicMap);
      }
      if (selectedInterval && processingRange.start >= selectedInterval.startFrame && processingRange.end <= selectedInterval.endFrame) {
        const patchedFrames = selectedInterval.frames.map((oldFrame, offset) => {
          const globalFrame = selectedInterval.startFrame + offset;
          if (globalFrame >= processingRange.start && globalFrame <= processingRange.end && newFrames[globalFrame]) {
            return { ...newFrames[globalFrame] };
          }
          return { ...oldFrame };
        });
        updateKeyframeInterval(selectedInterval.id, {
          frames: patchedFrames,
        });
      }
      setUnsaved(true);
      toast('success', `智能调脚完成`);
    } catch (error) {
      console.error('智能调脚失败:', error);
      toast('error', error instanceof Error ? error.message : '智能调脚失败');
    } finally {
      setIsSmartFootAdjusting(false);
    }
  }, [processingRange, selectedInterval, simulationFrames, originalSimulationData, jointKeyToUrdfName, displayFrames, rootTransforms, pushUndoSnapshot, updateKeyframeInterval]);

  // ===== 双脚调平 =====
  const handleFootLeveling = useCallback(async () => {
    if (!selectedInterval) {
      toast('warning', '请先选择关键帧区间');
      return;
    }
    if (processingRange.end <= processingRange.start) {
      toast('warning', '请先设置有效的帧区间');
      return;
    }

    setIsFootLeveling(true);
    try {
      if (!originalSimulationData) {
        toast('warning', '原始仿真数据尚未加载完成');
        return;
      }

      const payload = buildSimulationPayloadFromFrames(
        originalSimulationData,
        displayFrames,
        jointKeyToUrdfName,
        rootTransforms,
      );

      // const robotModel = taskDetail?.robot_model || 'booster_k1';

      const mj = mjHandleRef.current;
      let newRawData: SimulationJsonResponse | null = null;
      if (mj) {
        try {
          newRawData = runLocalTrajectoryOp(mj, payload, (editor) => {
            editor.footLeveling(processingRange.start, processingRange.end);
          });
        } catch (error) {
          console.error('[robotEdit] 本地双脚调平计算失败，回退到后端:', error);
          newRawData = null;
        }
      }
      // 已切换为纯本地计算，后端双脚调平接口注释保留备用
      // if (newRawData == null) {
      //   const result = await api.simulation.footLeveling({
      //     trajectory_data: payload,
      //     start_frame: processingRange.start,
      //     end_frame: processingRange.end,
      //     robot_model: robotModel,
      //   });
      //   newRawData = result.data as unknown as SimulationJsonResponse;
      // }
      if (newRawData == null) {
        toast('error', '本地计算未就绪（3D 模型未加载），请稍后重试');
        return;
      }

      const { rawData: parsedRawData, frames: newFrames, rootTransforms: newRt, jointKeyToUrdfName: newDynamicMap } = loadLocalSimulationCsv(
        newRawData,
        currentFieldNames,
      );
      pushUndoSnapshot();
      setOriginalSimulationData(parsedRawData);
      setSimulationFrames(newFrames);
      setRootTransforms(newRt);
      if (Object.keys(newDynamicMap).length > 0) {
        setJointKeyToUrdfName(newDynamicMap);
      }
      if (selectedInterval && processingRange.start >= selectedInterval.startFrame && processingRange.end <= selectedInterval.endFrame) {
        const patchedFrames = selectedInterval.frames.map((oldFrame, offset) => {
          const globalFrame = selectedInterval.startFrame + offset;
          if (globalFrame >= processingRange.start && globalFrame <= processingRange.end && newFrames[globalFrame]) {
            return { ...newFrames[globalFrame] };
          }
          return { ...oldFrame };
        });
        updateKeyframeInterval(selectedInterval.id, {
          frames: patchedFrames,
        });
      }
      setUnsaved(true);
      toast('success', '双脚调平完成');
    } catch (error) {
      console.error('双脚调平失败:', error);
      toast('error', error instanceof Error ? error.message : '双脚调平失败');
    } finally {
      setIsFootLeveling(false);
    }
  }, [processingRange, selectedInterval, simulationFrames, originalSimulationData, jointKeyToUrdfName, displayFrames, rootTransforms, pushUndoSnapshot, updateKeyframeInterval]);

  // ===== ZMP风险检测 =====
  const handleZmpRiskCheck = useCallback(async () => {
    if (!selectedInterval) {
      toast('warning', '请先选择关键帧区间');
      return;
    }
    if (processingRange.end <= processingRange.start) {
      toast('warning', '请先设置有效的帧区间');
      return;
    }
    if (processingRange.end - processingRange.start < 2) {
      toast('warning', 'ZMP检测至少需要3帧');
      return;
    }

    setIsZmpChecking(true);
    try {
      if (!originalSimulationData) {
        toast('warning', '原始仿真数据尚未加载完成');
        return;
      }

      const payload = buildSimulationPayloadFromFrames(
        originalSimulationData,
        displayFrames,
        jointKeyToUrdfName,
        rootTransforms,
      );

      // const robotModel = taskDetail?.robot_model || 'booster_k1';

      const mj = mjHandleRef.current;
      let result: ZmpRiskCheckResponse | null = null;
      if (mj) {
        try {
          const editor = createTrajectoryEditor(mj);
          if (editor) {
            editor.loadFromArray(payload);
            result = editor.zmpRiskCheck(processingRange.start, processingRange.end) as unknown as ZmpRiskCheckResponse;
          }
        } catch (error) {
          console.error('[robotEdit] 本地ZMP检测失败，回退到后端:', error);
          result = null;
        }
      }
      // 已切换为纯本地计算，后端ZMP风险检测接口注释保留备用
      // if (result == null) {
      //   result = await api.simulation.zmpRiskCheck({
      //     trajectory_data: payload,
      //     start_frame: processingRange.start,
      //     end_frame: processingRange.end,
      //     robot_model: robotModel,
      //   });
      // }
      if (result == null) {
        toast('error', '本地计算未就绪（3D 模型未加载），请稍后重试');
        return;
      }

      setZmpReport({
        score: result.score,
        high_risk: result.high_risk,
        warning_risk: result.warning_risk,
        by_direction: result.by_direction as Record<string, number[]>,
        details: (result.details || []) as Array<{
          frame_idx: number;
          level: string;
          direction: string;
          zmp_margin: number;
          com_margin: number;
          zmp: number[];
          com: number[];
          reasons: string[];
        }>,
        worst_text: result.worst_text,
        summary: result.summary,
      });
      setShowZmpModal(true);
      toast('success', `ZMP风险检测完成：评分 ${result.score}/100`);
    } catch (error) {
      console.error('ZMP风险检测失败:', error);
      toast('error', error instanceof Error ? error.message : 'ZMP风险检测失败');
    } finally {
      setIsZmpChecking(false);
    }
  }, [processingRange, selectedInterval, simulationFrames, originalSimulationData, jointKeyToUrdfName, displayFrames, rootTransforms]);

  // ===== 一键保存视频 =====
  const handleSaveVideo = useCallback(async () => {
    // --- 前置校验 ---
    if (displayFrames.length === 0) {
      toast('warning', '没有帧数据，无法导出视频');
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) {
      toast('warning', '3D画布未就绪，请稍后重试');
      return;
    }
    if (typeof VideoEncoder === 'undefined' || typeof VideoFrame === 'undefined') {
      if (!window.isSecureContext) {
        toast('error', '视频导出需要 HTTPS 安全上下文，当前页面通过 HTTP 访问，请使用 HTTPS 部署或联系管理员');
      } else {
        toast('error', '当前浏览器不支持 WebCodecs，请使用最新版 Chrome 或 Edge 浏览器导出 MP4');
      }
      return;
    }

    // --- 保存播放状态 ---
    const wasPlaying = playing;
    const savedFrame = currentFrameRef.current;
    if (wasPlaying) {
      pausingSyncRef.current = true;
      const actualFrameRaw = getMujocoFrameRef.current?.() ?? currentFrameRef.current;
      const actualFrame = Math.max(0, Math.min(actualFrameRaw, playbackMaxFrame));
      flushSync(() => {
        currentFrameRef.current = actualFrame;
        updatePlaybackDom(actualFrame);
        setCurrentFrame(actualFrame);
        setPlaying(false);
      });
      await new Promise<void>(r => requestAnimationFrame(r));
      pausingSyncRef.current = false;
    }

    setIsExportingVideo(true);
    const totalFrames = displayFrames.length;
    const fps = 30;
    toast('info', `开始导出视频：${totalFrames} 帧`);

    let encoder: VideoEncoder | null = null;
    let muxer: Muxer<ArrayBufferTarget> | null = null;

     try {
      // --- 分辨率（宏块对齐 + AVC Level 4.2 上限校验） ---
      // 宏块大小 16×16，coded area = ceil(w/16)*16 * ceil(h/16)*16
      // AVC Level 4.2 最大编码面积约 2,228,224 像素（8,704 宏块），覆盖 1920×1080+
      const MB = 16;
      let width = canvas.width;
      let height = canvas.height;
      // 向上对齐到宏块边界
      width = Math.ceil(width / MB) * MB;
      height = Math.ceil(height / MB) * MB;
      if (width < 64 || height < 64) {
        width = 1280;
        height = 720;
      }
      // 超过 AVC Level 4.2 上限时等比缩放
      const MAX_MACROBLOCKS = 8704; // Level 4.2
      let macroblocks = (width / MB) * (height / MB);
      if (macroblocks > MAX_MACROBLOCKS) {
        const scale = Math.sqrt(MAX_MACROBLOCKS / macroblocks);
        width = Math.ceil((width * scale) / MB) * MB;
        height = Math.ceil((height * scale) / MB) * MB;
      }

      // --- 初始化 Muxer + VideoEncoder ---
      muxer = new Muxer({
        target: new ArrayBufferTarget(),
        video: { codec: 'avc', width, height },
        fastStart: 'in-memory',
      });

      encoder = new VideoEncoder({
        output: (chunk, meta) => muxer!.addVideoChunk(chunk, meta),
        error: (e) => { throw e; },
      });
      encoder.configure({
        codec: 'avc1.42002A', // AVC Baseline Level 4.2，最大编码面积 ~2.2M 像素
        width,
        height,
        bitrate: 5_000_000,
        framerate: fps,
      });

      const frameIntervalUs = 1_000_000 / fps; // 33333 μs per frame

      // --- 逐帧渲染并编码 ---
      for (let i = 0; i < totalFrames; i++) {
        // encoder 已关闭（如因 codec 不支持而失败）则提前终止
        if (encoder.state !== 'configured') break;

        // 同步设置当前帧（仅更新3D渲染，不驱动时间轴UI）
        flushSync(() => {
          setCurrentFrame(i);
          currentFrameRef.current = i;
        });

        // 等待两个 rAF 确保 Three.js 渲染完成
        await new Promise<void>(r => requestAnimationFrame(r));
        await new Promise<void>(r => requestAnimationFrame(r));

        // 从 canvas 创建 VideoFrame 并送入编码器
        const frame = new VideoFrame(canvas, {
          timestamp: i * frameIntervalUs,
          duration: frameIntervalUs,
        });
        try {
          encoder.encode(frame, { keyFrame: i % (fps * 2) === 0 });
        } finally {
          frame.close();
        }
      }

      // --- 等待编码完成 ---
      await encoder.flush();
      muxer.finalize();

      // --- 构建 Blob 并触发下载 ---
      const buffer = (muxer.target as ArrayBufferTarget).buffer;
      const blob = new Blob([buffer], { type: 'video/mp4' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (dance?.name ?? 'robot_animation').replace(/[^a-zA-Z0-9一-鿿_-]/g, '_');
      a.download = `${safeName}_${Date.now()}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      const durationSec = (totalFrames / fps).toFixed(1);
      toast('success', `视频导出完成：${totalFrames}帧 / ${durationSec}秒`);

    } catch (err: any) {
      console.error('[SaveVideo] export failed:', err);
      toast('error', err?.message ? `视频导出失败：${err.message}` : '视频导出失败，请重试');
    } finally {
      // --- 释放资源 ---
      if (encoder && encoder.state !== 'closed') {
        try { encoder.close(); } catch { /* ignore */ }
      }
      encoder = null;
      muxer = null;

      // --- 恢复状态 ---
      setCurrentFrame(savedFrame);
      currentFrameRef.current = savedFrame;
      updatePlaybackDom(savedFrame);
      if (wasPlaying) {
        setPlaying(true);
      }
      setIsExportingVideo(false);
    }
  }, [displayFrames, playing, playbackMaxFrame, dance?.name, setCurrentFrame, updatePlaybackDom, setPlaying]);

  // Timeline mouse handlers
  const onTimelineMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    if (resizingEdge) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const scrollLeft = e.currentTarget.scrollLeft;
    const clickX = e.clientX - rect.left + scrollLeft;
    const clickFrame = x2f(clickX);

    const clickedInterval = keyframeIntervals.find(i => clickFrame >= i.startFrame && clickFrame <= i.endFrame);
    if (clickedInterval && !e.shiftKey) {
      setSelectedIntervalId(clickedInterval.id);
      setDragSelection(null);
      setProcessingRange({
        start: clickedInterval.startFrame,
        end: clickedInterval.endFrame,
      });
      setFrameImmediate(clickFrame);
      return;
    }

    // 已选中插帧区间后，禁止拖拽选中帧区间
    if (selectedIntervalId) return;

    e.preventDefault(); // 阻止浏览器默认行为（文本选择/滚动），Mac 上尤其必要
    dragStartFrame.current = clickFrame;
    setDragSelection({ start: clickFrame, end: clickFrame });
    setProcessingRange({ start: clickFrame, end: clickFrame });
    setIsDragging(true);
    setSelectedIntervalId(null);
    if (playing) setFrameImmediate(currentFrameRef.current);
    setPlaying(false);
  }, [resizingEdge, x2f, keyframeIntervals, setSelectedIntervalId, selectedIntervalId]);

  const onTimelineMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const scrollLeft = e.currentTarget.scrollLeft;
    const mouseX = e.clientX - rect.left + scrollLeft;
    const frame = x2f(mouseX);

    if (isDragging) {
      const newStart = Math.min(dragStartFrame.current, frame);
      const newEnd = Math.max(dragStartFrame.current, frame);
      // 拖拽时实时同步：输入框、文字区间和时间轴高亮共用同一范围。
      syncProcessingRange(newStart, newEnd);
      setFrameImmediate(frame);
    }

    if (resizingEdge) {
      const interval = keyframeIntervals.find(i => i.id === resizingEdge.intervalId);
      if (!interval) return;
      const updates: Partial<KeyframeInterval> = {};
      if (resizingEdge.edge === 'start' && frame < interval.endFrame - 2) {
        updates.startFrame = frame;
        const newLen = interval.endFrame - frame + 1;
        const newFrames = Array.from({ length: newLen }, (_, i) =>
          i < interval.frames.length ? interval.frames[i + (interval.startFrame - frame)] || { ...emptyJoints() } : { ...emptyJoints() }
        );
        updates.frames = newFrames;
      } else if (resizingEdge.edge === 'end' && frame > interval.startFrame + 2) {
        updates.endFrame = frame;
        const newLen = frame - interval.startFrame + 1;
        const newFrames = Array.from({ length: newLen }, (_, i) =>
          i < interval.frames.length ? interval.frames[i] : { ...emptyJoints() }
        );
        updates.frames = newFrames;
      }
      if (Object.keys(updates).length > 0) {
        updateKeyframeInterval(interval.id, updates);
        if (interval.id === selectedIntervalId) {
          setProcessingRange({
            start: updates.startFrame ?? interval.startFrame,
            end: updates.endFrame ?? interval.endFrame,
          });
        }
        setUnsaved(true);
      }
    }
  }, [isDragging, resizingEdge, x2f, keyframeIntervals, updateKeyframeInterval, syncProcessingRange, selectedIntervalId]);

  const onTimelineMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      if (dragSelection) {
        const start = Math.min(dragSelection.start, dragSelection.end);
        const end = Math.max(dragSelection.start, dragSelection.end);

        // 不论区间长度，松开后都保留并同步选择高亮；
        // “设为插帧区间”按钮仍只在至少 3 帧时显示。
        syncProcessingRange(start, end);
      }
    }
    if (resizingEdge) {
      setResizingEdge(null);
    }
  }, [isDragging, dragSelection, resizingEdge, syncProcessingRange]);

  const onEdgeResizeStart = useCallback((e: React.MouseEvent, intervalId: string, edge: 'start' | 'end') => {
    // 已禁用：禁止通过拖拽修改插帧区间，只能通过输入框修改
    // e.stopPropagation();
    // setResizingEdge({ intervalId, edge });
  }, []);

  const doSave = async () => {
    // 构造提交数据
    const committedFrames = displayFrames.map(frame => ({ ...frame }));
    const committedRoots = rootTransforms.map(rt => ({
      pos: [...rt.pos] as [number, number, number],
      quat: [...rt.quat] as [number, number, number, number],
    }));

    // 调用后端 API 保存
    setShowSaveConfirm(false);
    try {
      const userId = api.getStoredUserId();
      const simulationId = taskDetail?.simulation_id;
      if (!simulationId) {
        toast('warning', '缺少 simulation_id');
        return;
      }
      if (!originalSimulationData) {
        toast('warning', '原始仿真数据尚未加载完成');
        return;
      }
      const payload = buildSimulationPayloadFromFrames(originalSimulationData, committedFrames, jointKeyToUrdfName, committedRoots);
      await api.post(`/api/v1/simulation/${simulationId}/upload`, payload, {
        headers: { 'x-user-id': userId },
      });

      setOriginalSimulationData(payload);
      setSimulationFrames(committedFrames);
      setRootTransforms(committedRoots);
      originalFramesRef.current = committedFrames.map(f => ({ ...f }));
      originalRootTransformsRef.current = committedRoots.map(rt => ({
        pos: [...rt.pos] as [number, number, number],
        quat: [...rt.quat] as [number, number, number, number],
      }));
      bumpOriginalFramesVersion();
      setBaseline({
        keyframes: [...keyframes],
        intervals: JSON.parse(JSON.stringify(keyframeIntervals)),
      });
      setUnsaved(false);
      undoStackRef.current = [];
      jointDragActiveRef.current = false;
      baseEditActiveRef.current = false;
      toast('success', '已保存所有修改');
      navigate(-1);
    } catch (error) {
      console.error('Save failed:', error);
      toast('error', '保存失败，请稍后重试');
    }
  };

  // 本地模式：未加载 CSV 时显示上传界面
  if (loadingTask) return <div className="flex items-center justify-center h-full bg-slate-50"><p className="text-slate-400">任务详情加载中...</p></div>;
  if (!dance) return <div className="flex items-center justify-center h-full bg-slate-50"><p className="text-slate-400">舞蹈不存在</p></div>;

  const waistJoints = activeJointDefs.filter(j => j.key.includes('waist'));
  const dragSelLeft = dragSelection ? Math.min(dragSelection.start, dragSelection.end) : 0;
  const dragSelRight = dragSelection ? Math.max(dragSelection.start, dragSelection.end) : 0;
  // 区间包含首尾两帧，因此帧数必须 +1。
  const dragSelWidth = dragSelection ? dragSelRight - dragSelLeft + 1 : 0;

  const handleDragSelLeftChange = (val: number) => {
    if (!dragSelection || !Number.isFinite(val)) return;
    const newStart = Math.max(0, Math.min(Math.trunc(val) - 1, dragSelRight));
    syncProcessingRange(newStart, dragSelRight);
  };

  const handleDragSelRightChange = (val: number) => {
    if (!dragSelection || !Number.isFinite(val)) return;
    const newEnd = Math.max(dragSelLeft, Math.min(Math.trunc(val) - 1, playbackMaxFrame));
    syncProcessingRange(dragSelLeft, newEnd);
  };

  const handleProcessingStartChange = (val: number) => {
    if (!Number.isFinite(val)) return;
    const newStart = Math.max(0, Math.min(Math.trunc(val) - 1, processingRange.end));
    // 仅在输入有效值时才同步，避免输入过程中实时触发
    if (newStart !== processingRange.start) {
      syncProcessingRange(newStart, processingRange.end);
    }
  };

  const handleProcessingEndChange = (val: number) => {
    if (!Number.isFinite(val)) return;
    const newEnd = Math.max(processingRange.start, Math.min(Math.trunc(val) - 1, playbackMaxFrame));
    // 仅在输入有效值时才同步，避免输入过程中实时触发
    if (newEnd !== processingRange.end) {
      syncProcessingRange(processingRange.start, newEnd);
    }
  };

  const adjustFrame = (delta: number) => {
    // 使用 DOM 当前值作为基准，避免 React state 滞后导致跳帧
    const domFrame = playbackFrameInputRef.current
      ? Number(playbackFrameInputRef.current.value) - 1
      : currentFrame;
    // 当存在选中区间时，在区间内部循环移动
    if (selectedInterval && !playAll) {
      const start = selectedInterval.startFrame;
      const end = selectedInterval.endFrame;
      const next = domFrame + delta;
      if (next > end) setFrameImmediate(start);
      else if (next < start) setFrameImmediate(end);
      else setFrameImmediate(next);
    } else {
      // 全局模式下正常限制
      setFrameImmediate(Math.max(0, Math.min(domFrame + delta, playbackMaxFrame)));
    }
  };

  const totalWidth = simulationFrameCount * ppf;

  return (
    <div className="h-screen flex flex-col bg-[#F8FAFC] text-slate-800 overflow-hidden">
      {/* ===== HEADER ===== */}
      <div className="h-12 bg-white border-b border-slate-100 flex items-center justify-between px-4 shrink-0 z-50 relative">
        <div className="flex items-center gap-3">
          <button onClick={() => unsaved ? setShowSaveConfirm(true) : navigate(-1)} className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-text-secondary hover:text-brand hover:bg-brand-50 rounded-lg transition-colors">
            <ArrowLeft size={16} /> <span className="hidden sm:inline">返回</span>
          </button>
          <div className="w-px h-5 bg-slate-200" />
          <span className="text-sm font-semibold text-text-primary">{dance.name}</span>
          <span className="text-xs text-text-muted">关键帧区间编辑</span>
          <span className="text-[10px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full font-mono">{simulationFrameCount} 帧 / {danceDurations.toFixed(2)}s @ {FPS}fps</span>
        </div>
        <div className="flex items-center gap-3">
          {/* Save buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={undoStep}
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-amber-200 text-amber-600 rounded-lg text-sm font-medium hover:bg-amber-50 transition-colors"
              title="撤销最近一次确认性操作（保存/插帧/调腰等）"
            >
              <RotateCcw size={14} /> 撤销一步操作
            </button>
            <button
              onClick={resetAllFrames}
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-red-200 text-red-500 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors"
              title="恢复所有帧到 CSV 原始值"
            >
              <RefreshCw size={14} /> 撤销所有操作
            </button>
            <button
              onClick={handleRestoreBackup}
              disabled={isPythonBusy}
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-blue-200 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="从服务端拉取初始备份数据，还原所有帧到初始状态"
            >
              {isRestoringBackup ? <RefreshCw size={14} className="animate-spin" /> : <RotateCcw size={14} />}
              {isRestoringBackup ? '还原中...' : '一键还原初始帧'}
            </button>
            <button
              onClick={saveCurrentFrame}
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
            >
              保存当前帧
            </button>
            <button 
              onClick={saveAll}
              className="flex items-center gap-2 px-4 py-2 bg-brand text-white rounded-lg text-sm font-bold hover:bg-brand-dark transition-colors shadow-md"
            >
              <Save size={16} /> 保存所有修改
            </button>
          </div>
        </div>
      </div>
      {/* ===== MAIN CONTENT (3-column) ===== */}
      <div className="flex-1 flex overflow-hidden">

        {/* ===== LEFT: Robot Preview ===== */}
        <div className="flex-1 bg-gradient-to-b from-slate-900 to-slate-800 flex flex-col relative min-w-0">
          <div className="flex-1 relative flex items-center justify-center p-4 overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_60%,rgba(79,106,240,0.06)_0%,transparent_60%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_100%,rgba(79,106,240,0.1)_0%,transparent_40%)]" />
            <div className="absolute top-4 left-4 z-10 rounded-full bg-white/10 px-3 py-1 text-[10px] font-medium text-white/70 backdrop-blur-sm border border-white/10">
              3D 机器人预览
            </div>
            <div className="absolute top-4 right-4 z-10 rounded-full bg-black/30 px-3 py-1 text-[10px] font-mono text-white/70 backdrop-blur-sm border border-white/10">
              {dance.robotModel}
            </div>
            <div className="w-full h-full min-h-[420px]">
              <ErrorBoundary>
                {supportsMujoco ? (
                  <MujocoViewer
                    currentFrame={displayFrame}
                    simulationFrames={displayFrames}
                    rootTransforms={rootTransforms}
                    jointKeyToUrdfName={jointKeyToUrdfName}
                    controlsRef={controlsRef}
                    onMjReady={handleMjReady} tick={tick}
                    playing={playing}
                    speed={speed}
                    robotModel={selectedRobotModelId}
                    playbackRange={{
                      start: selectedInterval && !playAll ? selectedInterval.startFrame : 0,
                      end: selectedInterval && !playAll ? selectedInterval.endFrame : playbackMaxFrame,
                    }}
                    onGetCurrentFrameRef={(getFrame) => {
                      getMujocoFrameRef.current = getFrame;
                    }}
                    onFrameChange={(f) => {
                      if (pausingSyncRef.current) return;
                      if (!playing) return;
                      currentFrameRef.current = f;
                      // 低频同步 React state（约每 200ms），避免高频重渲染
                      const now = performance.now();
                      if (now - lastStateSyncTimeRef.current >= 200) {
                        lastStateSyncTimeRef.current = now;
                        setCurrentFrame(f);
                      }
                    }}
                    onPlaybackFrame={handlePlaybackFrame}
                    onCanvasReady={(el) => { canvasRef.current = el; }}
                  />
                ) : (
                  <RobotFigure
                    joints={currentJoints}
                    controlsRef={controlsRef}
                    modelId={renderModelId}
                    jointNameMap={jointKeyToUrdfName}
                    rootTransform={currentRootTransform}
                    refRootPos={refRootPos}
                    onCanvasReady={(el) => { canvasRef.current = el; }}
                  />
                )}
              </ErrorBoundary>
            </div>
          </div>

          <div className="px-4 py-3 border-t border-white/10 bg-black/10 backdrop-blur-sm">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => controlsRef.current?.reset()}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white/10 hover:bg-white/15 text-white/90 rounded-lg text-[10px] font-medium transition-all border border-white/10"
                >
                  <RotateCcw size={11} /> 重置视角
                </button>
                <button
                  onClick={handleSaveVideo}
                  disabled={isExportingVideo || displayFrames.length === 0}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-medium transition-all border border-white/10 ${
                    isExportingVideo || displayFrames.length === 0
                      ? 'bg-white/5 text-white/30 cursor-not-allowed'
                      : 'bg-white/10 hover:bg-white/15 text-white/90'
                  }`}
                >
                  {isExportingVideo ? (
                    <><RefreshCw size={11} className="animate-spin" /> 导出中...</>
                  ) : (
                    <><Film size={11} /> 一键保存</>
                  )}
                </button>   
              </div>
              <span className="text-[10px] text-white/45">
                {isExportingVideo ? '视频导出中，请稍候...' : '左键旋转 · 中键缩放 · 右键平移'}
              </span>
            </div>
          </div>

          {/* Playback Controls */}
          <div className="px-4 pb-4 space-y-3">
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-3 border border-white/10">
              <div className="flex items-center gap-3 mb-3">
                {/* Global play/pause button */}
                <button
                  onClick={() => {
                    if (playing) {
                      // 暂停时从 MujocoViewer 获取实际渲染的帧号，确保精确同步
                      const actualFrameRaw = getMujocoFrameRef.current?.() ?? currentFrameRef.current;
                      const actualFrame = Math.max(0, Math.min(actualFrameRaw, playbackMaxFrame));

                      console.log('[pause-click]', {
                        actualFrame,
                        refFrame: currentFrameRef.current,
                        stateFrame: currentFrame,
                      });

                      pausingSyncRef.current = true;

                      flushSync(() => {
                        currentFrameRef.current = actualFrame;
                        updatePlaybackDom(actualFrame);
                        setCurrentFrame(actualFrame);
                        setPlaying(false);
                      });

                      // 等 React 完成暂停提交后再解除锁
                      // 用 requestAnimationFrame 挡住暂停点击附近残留的一次 R3F 回调
                      requestAnimationFrame(() => {
                        pausingSyncRef.current = false;
                      });

                      return;
                    }

                    pausingSyncRef.current = false;
                    setPlayAll(!selectedInterval);
                    setPlaying(true);
                  }}
                  className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors shadow-md shrink-0 ${playing ? 'bg-red-500 shadow-red-500/30' : 'bg-brand shadow-brand/30 hover:bg-brand-dark'}`}
                >
                  {playing ? <Pause size={18} className="text-white" /> : <Play size={18} className="text-white ml-0.5" />}
                </button>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span ref={playbackTimeLabelRef} className="text-xs font-mono text-white/90">{(currentFrame / FPS).toFixed(2)}s</span>
                      <div className="flex items-center gap-1 bg-white/10 rounded px-1.5 py-0.5">
                        <span className="text-[10px] text-white/40">帧</span>
                        <input
                          ref={playbackFrameInputRef}
                          type="number"
                          defaultValue={currentFrame + 1}
                          onChange={(e) => {
                            const v = Number(e.target.value) - 1;
                            setFrameImmediate(v);
                          }}
                          className="w-10 bg-transparent border-none outline-none text-[10px] font-mono text-white text-center"
                        />
                      </div>
                    </div>
                    <span className="text-xs font-mono text-white/50">{danceDurations.toFixed(2)}s</span>
                  </div>
                  <input ref={playbackSliderRef} type="range" defaultValue={currentFrame + 1} onChange={e => setFrameImmediate(Number(e.target.value) - 1)}
                    min={1} max={playbackMaxFrame + 1} step={1}
                    className="w-full h-1.5 bg-white/20 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:shadow-md" />
                </div>
              </div>

              {/* Adjustment Buttons Row */}
              <div className="flex items-center justify-center gap-2 border-t border-white/5 pt-2">
                <button onClick={() => adjustFrame(-20)} className="flex-1 py-1 bg-white/5 hover:bg-white/10 rounded text-[10px] text-white/60 transition-colors border border-white/5">-20</button>
                <button onClick={() => adjustFrame(-1)} className="flex-1 py-1 bg-white/5 hover:bg-white/10 rounded text-[10px] text-white/60 transition-colors border border-white/5">-1</button>
                <button onClick={() => adjustFrame(1)} className="flex-1 py-1 bg-white/5 hover:bg-white/10 rounded text-[10px] text-white/60 transition-colors border border-white/5">+1</button>
                <button onClick={() => adjustFrame(20)} className="flex-1 py-1 bg-white/5 hover:bg-white/10 rounded text-[10px] text-white/60 transition-colors border border-white/5">+20</button>
              </div>

              {playing && (
                <div className="flex items-center gap-1.5 mt-2 justify-center">
                  <div className="w-1.5 h-1.5 bg-red-400 rounded-full animate-pulse" />
                  <span className="text-[10px] text-white/60 font-medium">{playAll ? '全局播放中' : '区间播放中'}</span>
                </div>
              )}
            </div>
            {/* Speed */}
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-md rounded-xl px-3 py-2 border border-white/10">
              <span className="text-xs text-white/50 shrink-0">倍速</span>
              <div className="relative flex-1">
                <button onClick={() => setSpeedDropdownOpen(!speedDropdownOpen)}
                  className="w-full flex items-center justify-center gap-1 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold px-2 py-1 rounded-lg outline-none cursor-pointer transition-colors">
                  {speed}x <ChevronDown size={12} />
                </button>
                {speedDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setSpeedDropdownOpen(false)} />
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-28 bg-slate-800/95 backdrop-blur-xl border border-white/10 shadow-lg rounded-xl py-1.5 z-50">
                      {SPEED_OPTIONS.map(s => (
                        <button key={s} onClick={() => { setSpeed(s); setSpeedDropdownOpen(false); }}
                          className="w-full flex items-center px-2 py-1.5 text-xs hover:bg-white/10 transition-colors">
                          <div className="w-5 flex justify-center shrink-0">{speed === s && <Check size={14} className="text-brand stroke-[3]" />}</div>
                          <span className={`font-medium ${speed === s ? 'text-brand' : 'text-white/70'}`}>{s}x</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ===== CENTER: Joint Parameters ===== */}
        <div className="flex-1 bg-[#F8FAFC] flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto custom-scrollbar">

            {/* Current display info */}
            <div className="px-5 py-2.5 bg-gradient-to-r from-brand to-blue-600 text-white">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">当前预览帧</span>
                <span className="text-sm font-mono font-bold bg-white/20 px-2 py-0.5 rounded">
                  帧 {displayFrame + 1}
                </span>
              </div>
            </div>

            {/* Base Control 面板（参考 Python base_control_widget.py） */}
            <BaseControlPanel mj={mjHandle} currentFrame={displayFrame} rootTransform={currentRootTransform} onChanged={(nextRt) => { if (!baseEditActiveRef.current) { baseEditActiveRef.current = true; pushUndoSnapshot(); } handleBaseTransformChange(displayFrame, nextRt); }} onBlurCommit={() => { baseEditActiveRef.current = false; }} onResetPos={handleBaseResetPos} onResetRot={handleBaseResetRot} onResetAll={handleBaseResetAll} syncTick={baseSyncTick} />
            {/* Joint Parameters (all joints) - grouped layout */}
            <div className="border-b border-slate-100 bg-white">
              <div
                role="button"
                tabIndex={0}
                onClick={() => setParamsExpanded(!paramsExpanded)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setParamsExpanded(!paramsExpanded);
                  }
                }}
                className="w-full flex items-center gap-2 px-5 py-3 hover:bg-slate-50 transition-colors cursor-pointer"
              >
                {paramsExpanded ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
                <span className="text-sm font-bold text-slate-800">关节参数调节</span>
                <SlidersHorizontal size={14} className="text-slate-400" />
                <div className="flex items-center gap-2 ml-4">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); resetCurrentFrame(); }}
                    className="flex items-center gap-1 px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded text-[10px] font-bold transition-colors border border-slate-200"
                    title="重置当前帧所有关节到 CSV 原始值"
                  >
                    <RotateCcw size={10} /> 重置当前帧
                  </button>
                </div>
                <span className="ml-auto text-xs font-medium text-brand bg-brand-50 px-2 py-0.5 rounded-full">{activeJointDefs.length}</span>
              </div>
              {paramsExpanded && (
                <div className="px-5 pb-5 space-y-2">
                  {activeJointDefs.map(joint => {
                    const val = currentJoints[joint.key];
                    const isModified = unsavedJointKeys.has(joint.key);
                    return (
                      <div key={joint.key} className={`flex items-center gap-3 p-2 rounded-lg border transition-colors ${isModified ? 'bg-orange-50 border-orange-300' : 'bg-slate-50 border-slate-100'}`}>
                        <div className="w-32 shrink-0">
                          <span className={`text-[11px] font-bold block leading-tight ${isModified ? 'text-orange-600' : 'text-slate-800'}`}>{joint.label}</span>
                          <span className={`text-[9px] font-medium block uppercase tracking-wider truncate ${isModified ? 'text-orange-400' : 'text-slate-400'}`}>{isModified ? '未保存' : joint.labelEn}</span>
                        </div>
                        <input type="range" value={val} onChange={e => chJoint(joint.key, Number(e.target.value))} onPointerUp={endJointEdit} onMouseUp={endJointEdit} onTouchEnd={endJointEdit} min={joint.min} max={joint.max} step={0.01} className={`flex-1 h-2 ${isModified ? 'accent-orange-500' : 'accent-brand'}`} />
                        <div className="flex items-center gap-1.5">
                          <input type="number" value={val} step={0.01} min={joint.min} max={joint.max}
                            onBlur={endJointEdit}
                            onKeyDown={(e) => { if (e.key === 'Enter') { endJointEdit(); e.currentTarget.blur(); } }}
                            onChange={e => { const n = Number(e.target.value); if (!Number.isFinite(n)) return; chJoint(joint.key, n); }}
                            className={`w-20 text-right text-xs font-mono font-bold rounded-md px-1.5 py-0.5 outline-none transition-colors [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield] ${isModified ? 'bg-orange-50 border-orange-300 text-orange-700 focus:border-orange-500' : 'bg-white border border-slate-200 text-slate-700 focus:border-brand'}`} />
                          <button onClick={() => resetJoint(joint.key)}
                            className="w-5 h-5 flex items-center justify-center rounded hover:bg-slate-200 transition-colors" title="复位">
                            <RotateCcw size={8} className="text-slate-400" />
                          </button>
                          <button onClick={() => { setCurveJointKey(joint.key); setCurveExpanded(true); setTimeout(() => { curvePanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 50); }}
                            className="flex items-center gap-0.5 px-1.5 py-0.5 rounded hover:bg-blue-50 transition-colors border border-blue-200 text-blue-500" title="查看该关节的轨迹曲线">
                            <LineChart size={10} />
                            <span className="text-[9px] font-medium">曲线</span>
                          </button>
                          <span className="text-[9px] text-slate-400 font-mono whitespace-nowrap">[{joint.min}, {joint.max}]</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>


            {/* Joint Curve Panel */}
            <div ref={curvePanelRef} className="border-b border-slate-100 bg-white">
              <div
                role="button"
                tabIndex={0}
                onClick={() => setCurveExpanded(!curveExpanded)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setCurveExpanded(!curveExpanded);
                  }
                }}
                className="w-full flex items-center gap-2 px-5 py-3 hover:bg-slate-50 transition-colors cursor-pointer"
              >
                {curveExpanded ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
                <span className="text-sm font-bold text-slate-800">关节曲线</span>
                <LineChart size={14} className="text-slate-400" />
              </div>
              {curveExpanded && (
                <JointCurvePanel
                  frames={displayFrames}
                  currentFrame={displayFrame}
                  jointKey={curveJointKey}
                  onJointKeyChange={setCurveJointKey}
                  jointDefs={activeJointDefs}
                />
              )}
            </div>

          </div>
        </div>

        {/* ===== RIGHT: Frame List ===== */}
        <div className="w-[320px] bg-white border-l border-slate-200 flex flex-col shrink-0 shadow-[-4px_0_24px_-12px_rgba(0,0,0,0.08)] z-10">
          <div className="flex-1 overflow-y-auto custom-scrollbar">
            <div className="sticky top-0 bg-white/95 backdrop-blur-sm z-10 border-b border-slate-100 px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Layers size={16} className="text-slate-500" />
                  <span className="text-sm font-bold text-slate-800">帧列表</span>
                  <span className="text-xs font-medium text-brand bg-brand-50 px-1.5 py-0.5 rounded-full">
                    {selectedInterval ? selectedInterval.endFrame - selectedInterval.startFrame + 1 : simulationFrameCount}
                  </span>
                </div>
                {selectedInterval && (
                  <button
                    onClick={() => setShowInterpolateModal(true)}
                    disabled={isPythonBusy}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-brand text-white text-xs font-bold rounded-lg hover:bg-brand-dark transition-colors shadow-sm disabled:opacity-50"
                  >
                    {isInterpolating ? <RefreshCw size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                    {isInterpolating ? '插帧中...' : '确认插帧'}
                  </button>
                )}
              </div>
            </div>
            <div className="p-2">
              <div className="flex flex-col gap-1">
                {Array.from({ length: simulationFrameCount }, (_, i) => i)
                  .filter(frame => {
                    if (!selectedInterval) return true;
                    return frame >= selectedInterval.startFrame && frame <= selectedInterval.endFrame;
                  })
                  .map(frame => {
                  const isCurrent = currentFrame === frame;
                  const inInterval = keyframeIntervals.some(interval => frame >= interval.startFrame && frame <= interval.endFrame);
                  const intervalColor = keyframeIntervals.find(interval => frame >= interval.startFrame && frame <= interval.endFrame)?.color;
                  const isKeyframe = keyframes.some(k => k.frame === frame);
                  const isUnsaved = unsavedFrameIndices.has(frame);
                  const isDefaultKF = inInterval && selectedInterval && (frame === selectedInterval.startFrame || frame === selectedInterval.endFrame);
                  const isEdited = editedFrameSet.has(frame) && !isUnsaved && !inInterval;

                  return (
                    <div
                      key={frame}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        setFrameImmediate(frame);
                        if (inInterval) {
                          setSelectedFrameInInterval(frame);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setFrameImmediate(frame);
                          if (inInterval) {
                            setSelectedFrameInInterval(frame);
                          }
                        }
                      }}
                      className={`
                        relative flex items-center justify-between px-3 py-2 rounded-lg text-xs font-mono font-bold transition-all group cursor-pointer
                        ${isCurrent
                          ? 'bg-brand text-white shadow-sm'
                          : inInterval
                            ? 'bg-white border hover:bg-slate-50'
                            : isUnsaved
                              ? 'bg-white border hover:bg-slate-50'
                              : isEdited
                                ? 'bg-emerald-50/60 text-slate-500 hover:bg-emerald-100/60'
                                : 'bg-slate-50 text-slate-400 hover:bg-slate-100'}
                      `}
                      style={!isCurrent && (inInterval || isUnsaved) ? { borderColor: isUnsaved ? '#F97316' : isDefaultKF || isKeyframe ? '#F59E0B' : intervalColor, borderWidth: isDefaultKF || isUnsaved || isKeyframe ? '2px' : '1px' } : undefined}
                    >
                      <div className="flex items-center gap-1.5">
                        {(isDefaultKF || isKeyframe) && <div className="w-2 h-2 bg-yellow-500 rotate-45 shrink-0" />}
                        {isUnsaved && !isKeyframe && !isDefaultKF && <div className="w-2 h-2 bg-orange-500 rounded-full shrink-0" />}
                        {isEdited && <div className="w-2 h-2 bg-emerald-400 rounded-full shrink-0" />}
                        <span>帧 {frame + 1}</span>
                      </div>
                      {(inInterval || isUnsaved || isEdited) && (
                        <span className="text-[10px]" style={{ color: isUnsaved && !isDefaultKF ? '#F97316' : isDefaultKF || isKeyframe ? '#F59E0B' : isEdited ? '#10B981' : intervalColor }}>
                          {isUnsaved && !isDefaultKF ? '未保存' : isDefaultKF ? '默认关键帧' : isKeyframe ? '关键帧' : isEdited ? '已编辑' : '插帧区间内'}
                        </span>
                      )}
                      {/* Hover/Current button - 设为关键帧（首尾帧不可操作） */}
                      {inInterval && !isKeyframe && !isDefaultKF && (
                        <span className={`absolute right-2 transition-opacity ${isCurrent ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                        setKeyframes(prev => [...prev, {
                                id: `kf-${frame}-${Date.now()}`,
                                frame: frame,
                                joints: { ...getJointsAtFrame(frame) }
                              }]);
                              setUnsaved(true);
                              toast('success', `已设置帧 ${frame + 1} 为关键帧`);
                            }}
                            className="text-[10px] px-1.5 py-0.5 bg-brand text-white rounded hover:bg-brand-dark"
                          >
                            设为关键帧
                          </button>
                        </span>
                      )}
                      {inInterval && isKeyframe && !isDefaultKF && (
                        <span className={`absolute right-2 transition-opacity ${isCurrent ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setKeyframes(prev => prev.filter(k => k.frame !== frame));
                              setUnsaved(true);
                              toast('success', `已取消帧 ${frame + 1} 的关键帧设定`);
                            }}
                            className="text-[10px] px-1.5 py-0.5 bg-red-500 text-white rounded hover:bg-red-600"
                          >
                            取消设为关键帧
                          </button>
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ===== BOTTOM: Timeline (Frame-based) ===== */}
      <div className="h-[220px] bg-white border-t border-slate-200 flex flex-col shrink-0 z-20 shadow-[0_-4px_24px_-12px_rgba(0,0,0,0.1)]">
        {/* Timeline Toolbar */}
        <div className="flex items-center gap-3 px-4 py-2 border-b border-slate-100 bg-slate-50/80">
          <Layers size={14} className="text-slate-500" />
          <span className="text-xs font-bold text-slate-700">帧轴</span>
          <span className="text-[10px] text-slate-400 font-mono">1s = {FPS}帧</span>
          <div className="w-px h-3 bg-slate-300" />

          {/* Set as keyframe button */}
          {dragSelection && dragSelWidth >= 2 && !isDragging && (
            <button onClick={setAsKeyframe}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors shadow-sm animate-fade-in">
              <Bookmark size={12} /> 设为插帧区间
            </button>
          )}

          {/* Cancel keyframe button when an interval is selected */}
          {selectedInterval && (
            <button onClick={() => {
              pushUndoSnapshot();
              deleteKeyframeInterval(selectedInterval.id);
              setSelectedIntervalId(null);
              setDragSelection(null);
              setKeyframes([]);
              setPersistedKeyframeFrames(new Set());
              setUnsaved(true);
              toast('success', `已取消「${selectedInterval.name}」的插帧区间设定`);
            }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors shadow-sm">
              <Bookmark size={12} /> 取消设为插帧区间
            </button>
          )}

          {/* Cancel keyframe button removed from toolbar as requested */}

          {/* Confirm interpolation button */}
          {selectedInterval && (
            <button onClick={() => setShowInterpolateModal(true)}
              disabled={isPythonBusy}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors shadow-sm disabled:opacity-50">
              {isInterpolating ? <RefreshCw size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              {isInterpolating ? '插帧中...' : '确认插帧'}
            </button>
          )}

          {dragSelection && dragSelWidth >= 2 && (
            <div className="flex items-center gap-1 text-[10px] font-medium text-brand bg-brand-50 px-2 py-0.5 rounded-lg border border-brand-200 font-mono">
              <span>帧</span>
              <input
                type="number"
                value={dragSelLeft + 1}
                onChange={(e) => handleDragSelLeftChange(Number(e.target.value))}
                className="w-10 bg-transparent border-none outline-none text-center focus:bg-white/50 rounded"
              />
              <span>~</span>
              <input
                type="number"
                value={dragSelRight + 1}
                onChange={(e) => handleDragSelRightChange(Number(e.target.value))}
                className="w-10 bg-transparent border-none outline-none text-center focus:bg-white/50 rounded"
              />
              <span>（共 {dragSelWidth} 帧）</span>
            </div>
          )}

          {isDragging && dragSelection && (
            <span className="text-[10px] font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-lg border border-blue-200 animate-pulse">
              拖拽中… 松开后点击「设为插帧区间」
            </span>
          )}

          {/* 帧区间输入（始终可见，默认全帧） */}
          <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white border border-slate-200 shadow-sm">
            <span className="text-[10px] font-bold text-slate-500 whitespace-nowrap">帧区间</span>
            <input
              type="number"
              defaultValue={processingRange.start + 1}
              key={`start-${processingRange.start}`}
              min={1}
              max={processingRange.end + 1}
              onBlur={(e) => {
                const val = Number(e.target.value);
                if (!Number.isFinite(val)) return;
                const newStart = Math.max(0, Math.min(Math.trunc(val) - 1, processingRange.end));
                syncProcessingRange(newStart, processingRange.end);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  (e.target as HTMLInputElement).blur();
                }
              }}
              className="w-10 text-center text-[10px] font-mono font-bold text-slate-700 bg-slate-50 border border-slate-100 rounded outline-none focus:border-brand"
            />
            <span className="text-[10px] text-slate-400">~</span>
            <input
              type="number"
              defaultValue={processingRange.end + 1}
              key={`end-${processingRange.end}`}
              min={processingRange.start + 1}
              max={playbackMaxFrame + 1}
              onBlur={(e) => {
                const val = Number(e.target.value);
                if (!Number.isFinite(val)) return;
                const newEnd = Math.max(processingRange.start, Math.min(Math.trunc(val) - 1, playbackMaxFrame));
                syncProcessingRange(processingRange.start, newEnd);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  (e.target as HTMLInputElement).blur();
                }
              }}
              className="w-10 text-center text-[10px] font-mono font-bold text-slate-700 bg-slate-50 border border-slate-100 rounded outline-none focus:border-brand"
            />
            <span className="text-[10px] text-slate-400 font-mono">共 {processingRange.end - processingRange.start + 1} 帧</span>
          </div>

          {/* 前后腰扶正 */}
          <button
            onClick={() => handleAnkleLockedBaseUpright('pitch')}
            disabled={isPythonBusy || processingRange.end <= processingRange.start}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg transition-colors border ${
              isAnkleLockedUprighting === 'pitch'
                ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border-slate-200'
            }`}
            title="锁定 ankle 世界位姿，只把基座 pitch 调到 0"
          >
            {isAnkleLockedUprighting === 'pitch' ? <RefreshCw size={12} className="animate-spin" /> : <Crosshair size={12} />}
            {isAnkleLockedUprighting === 'pitch' ? '前后腰扶正中...' : '前后腰扶正'}
          </button>

          {/* 左右腰扶正 */}
          <button
            onClick={() => handleAnkleLockedBaseUpright('roll')}
            disabled={isPythonBusy || processingRange.end <= processingRange.start}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg transition-colors border ${
              isAnkleLockedUprighting === 'roll'
                ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border-slate-200'
            }`}
            title="锁定 ankle 世界位姿，只把基座 roll 调到 0"
          >
            {isAnkleLockedUprighting === 'roll' ? <RefreshCw size={12} className="animate-spin" /> : <Crosshair size={12} />}
            {isAnkleLockedUprighting === 'roll' ? '左右腰扶正中...' : '左右腰扶正'}
          </button>

          {/* 智能调脚 */}
          <button
            onClick={handleSmartFootAdjust}
            disabled={isPythonBusy || processingRange.end <= processingRange.start}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg transition-colors border ${
              isSmartFootAdjusting
                ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border-slate-200'
            }`}
            title="智能调脚"
          >
            {isSmartFootAdjusting ? <RefreshCw size={12} className="animate-spin" /> : <Activity size={12} />}
            {isSmartFootAdjusting ? '调脚中...' : '智能调脚'}
          </button>

          {/* 双脚调平 */}
          <button
            onClick={handleFootLeveling}
            disabled={isPythonBusy || processingRange.end <= processingRange.start}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg transition-colors border ${
              isFootLeveling
                ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border-slate-200'
            }`}
            title="双脚调平"
          >
            {isFootLeveling ? <RefreshCw size={12} className="animate-spin" /> : <Footprints size={12} />}
            {isFootLeveling ? '双脚调平中...' : '双脚调平'}
          </button>

          {/* ZMP风险检测 */}
          <button
            onClick={handleZmpRiskCheck}
            disabled={isZmpChecking || processingRange.end - processingRange.start < 2}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg transition-colors border ${
              isZmpChecking
                ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border-slate-200'
            }`}
            title="ZMP风险检测（至少需要3帧）"
          >
            {isZmpChecking ? <RefreshCw size={12} className="animate-spin" /> : <AlertTriangle size={12} />}
            {isZmpChecking ? '检测中...' : 'ZMP风险检测'}
          </button>

          <div className="flex-1" />

          {/* Zoom */}
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-0.5 shadow-sm">
            <button onClick={() => setZoom(Math.max(0.5, zoom - 0.25))} className="w-7 h-7 flex items-center justify-center hover:bg-slate-100 rounded-md transition-colors">
              <ZoomOut size={14} className="text-slate-500" />
            </button>
            <span className="text-[10px] font-mono font-bold text-slate-600 w-10 text-center">{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom(Math.min(4, zoom + 0.25))} className="w-7 h-7 flex items-center justify-center hover:bg-slate-100 rounded-md transition-colors">
              <ZoomIn size={14} className="text-slate-500" />
            </button>
          </div>
        </div>

        {/* Timeline Canvas */}
        <div className="flex-1 relative bg-[#FAFBFC]"
          onMouseUp={onTimelineMouseUp}
          onMouseLeave={() => { if (isDragging || resizingEdge) onTimelineMouseUp(); setHoveredFrame(null); }}
        >
          <div
            className="absolute inset-0 overflow-x-auto timeline-scroll custom-scrollbar"
            style={{ userSelect: 'none', touchAction: 'none' } as React.CSSProperties}
            onMouseDown={onTimelineMouseDown}
            onMouseUp={onTimelineMouseUp}
            onMouseMove={(e) => {
              onTimelineMouseMove(e);
              const rect = e.currentTarget.getBoundingClientRect();
              const scrollLeft = e.currentTarget.scrollLeft;
              const mouseX = e.clientX - rect.left + scrollLeft;
              const frame = x2f(mouseX);
              if (keyframeIntervals.some(i => frame >= i.startFrame && frame <= i.endFrame)) {
                setHoveredFrame(frame);
              } else {
                setHoveredFrame(null);
              }
            }}
            onMouseEnter={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const scrollLeft = e.currentTarget.scrollLeft;
              const mouseX = e.clientX - rect.left + scrollLeft;
              const frame = x2f(mouseX);
              if (keyframeIntervals.some(i => frame >= i.startFrame && frame <= i.endFrame)) {
                setHoveredFrame(frame);
              }
            }}
            onMouseLeave={() => setHoveredFrame(null)}
          >
            <div className="relative h-full" style={{ width: `${totalWidth}px`, minHeight: '100%' }}>
              {/* Center track line - moved down to give space for buttons on top */}
              <div className="absolute top-[65%] left-0 right-0 h-1 bg-slate-200 -translate-y-1/2 rounded" />

              {/* Second markers (every 30 frames) */}
              {Array.from({ length: Math.floor(simulationFrameCount / FPS) + 1 }, (_, i) => i * FPS).map(f => (
                <div key={`sec-${f}`} className="absolute top-0 bottom-0 flex flex-col items-center" style={{ left: `${f2x(f)}px` }}>
                  <div className="w-px h-full bg-slate-300/60" />
                  <span className="absolute top-0.5 text-[10px] font-mono font-bold text-slate-500 bg-[#FAFBFC] px-1">{f + 1}</span>
                </div>
              ))}

              {/* 5-frame minor ticks */}
              {Array.from({ length: Math.floor(simulationFrameCount / 5) + 1 }, (_, i) => i * 5)
                .filter(f => f % FPS !== 0)
                .map(f => (
                  <div key={`tick-${f}`} className="absolute top-[55%] bottom-[25%]" style={{ left: `${f2x(f)}px` }}>
                    <div className="w-px h-full bg-slate-200" />
                  </div>
                ))}

              {/* Drag selection highlight */}
              {dragSelection && dragSelWidth > 0 && (
                <div className="absolute top-[50%] bottom-[10%] bg-brand/10 border-l-2 border-r-2 border-brand/40 rounded-sm z-[5]"
                  style={{ left: `${f2x(dragSelLeft)}px`, width: `${f2x(dragSelRight) - f2x(dragSelLeft) + ppf}px` }} />
              )}

              {/* Simulation Video Track (Background) */}
              <div className="absolute top-[80%] bottom-[10%] left-0 right-0 bg-slate-100/50 border-y border-slate-200/60 z-0 flex items-center px-4 overflow-hidden">
                <div className="flex items-center gap-2 opacity-40">
                  <Play size={10} className="text-slate-400" />
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">仿真视频原帧 (Simulation Video)</span>
                </div>
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent mx-4" />
                
              </div>

              {/* Keyframe interval bars */}
              {keyframeIntervals.map(interval => {
                const isSelected = interval.id === selectedIntervalId;
                const isResizingThis = resizingEdge?.intervalId === interval.id;
                return (
                  <div key={interval.id}
                    className={`absolute top-[55%] bottom-[25%] rounded-md z-10 transition-all cursor-pointer ${isSelected ? 'ring-2 ring-white ring-offset-1 shadow-lg' : 'hover:brightness-110'}`}
                    style={{
                      left: `${f2x(interval.startFrame)}px`,
                      width: `${f2x(interval.endFrame) - f2x(interval.startFrame) + ppf}px`,
                      backgroundColor: `${interval.color}${isSelected ? '40' : '25'}`,
                      borderTop: `3px solid ${interval.color}`,
                      borderBottom: `3px solid ${interval.color}`,
                    }}>
                    {/* Interval label */}
                    <div className="absolute inset-x-0 top-1 flex items-center justify-center pointer-events-none">
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm text-white truncate max-w-[90%]"
                        style={{ backgroundColor: `${interval.color}CC` }}>
                        {interval.name}
                      </span>
                    </div>
                    {/* Left resize handle - 已禁用拖拽 */}
                    {/* <div
                      className="absolute left-0 top-0 bottom-0 w-3 cursor-ew-resize z-20 hover:bg-white/30 rounded-l-md transition-colors"
                      onMouseDown={e => onEdgeResizeStart(e, interval.id, 'start')}
                      style={{ borderLeft: `3px solid ${interval.color}` }}
                    /> */}
                    {/* Right resize handle - 已禁用拖拽 */}
                    {/* <div
                      className="absolute right-0 top-0 bottom-0 w-3 cursor-ew-resize z-20 hover:bg-white/30 rounded-r-md transition-colors"
                      onMouseDown={e => onEdgeResizeStart(e, interval.id, 'end')}
                      style={{ borderRight: `3px solid ${interval.color}` }}
                    /> */}
                  </div>
                );
              })}

              {/* Edited frame range markers — 服务端已持久化的编辑历史 */}
              {editedRanges.map((range, idx) => (
                <div
                  key={`edited-${range.start}-${idx}`}
                  className="absolute top-[80%] bottom-[10%] rounded-sm z-[2] pointer-events-none"
                  style={{
                    left: `${f2x(range.start)}px`,
                    width: `${f2x(range.end) - f2x(range.start) + ppf}px`,
                    backgroundColor: 'rgba(16, 185, 129, 0.12)',
                    borderTop: '2px solid rgba(16, 185, 129, 0.35)',
                    borderBottom: '2px solid rgba(16, 185, 129, 0.35)',
                  }}
                />
              ))}

              {/* 关键帧位置标记（琥珀色菱形）— 仅关键帧但无实际关节改动的帧，与绿色变化针区分 */}
              {Array.from(persistedKeyframeFrames)
                .filter(f => !editedFrameSet.has(f))
                .map(f => (
                  <div key={`kfpin-${f}`} className="absolute top-[80%] -translate-y-1/2 z-[3] pointer-events-none"
                       style={{ left: `${f2x(f)}px` }}>
                    <div className="w-3 h-3 bg-amber-400/70 rotate-45 border border-white/50 shadow-sm" />
                  </div>
                ))}

              {/* Playback head (red line) */}
              <div ref={playbackHeadRef} className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-30" style={{ left: 0, transform: `translateX(${f2x(currentFrame)}px)` }}>
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[5px] border-r-[5px] border-t-[7px] border-l-transparent border-r-transparent border-t-red-500" />
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[5px] border-r-[5px] border-b-[7px] border-l-transparent border-r-transparent border-b-red-500" />
                <span ref={playbackHeadLabelRef} className="absolute top-4 left-1/2 -translate-x-1/2 text-[8px] font-mono text-red-500 whitespace-nowrap font-bold">
                  {currentFrame + 1}
                </span>
              </div>

              {/* Keyframe markers - diamond shape with hover button */}
              {keyframes.map(kf => {
                const isCurrent = kf.frame === currentFrame;
                return (
                  <div key={kf.id} className="absolute top-[65%] -translate-y-1/2 z-25 group" style={{ left: `${f2x(kf.frame)}px` }}>
                    <div 
                      onClick={() => setFrameImmediate(kf.frame)}
                      className={`w-4 h-4 rotate-45 border-2 border-white shadow-sm cursor-pointer transition-all ${isCurrent ? 'bg-brand scale-125 z-30' : 'bg-yellow-400 hover:scale-110'}`} 
                    />
                  </div>
                );
              })}

              {/* Sub-frame indicator - 已移除 */}
              {/* {selectedInterval && (
                <div className="absolute top-0 bottom-0 w-0.5 bg-yellow-400 z-25 opacity-70" style={{ left: `${f2x(currentSubFrame)}px` }}>
                  <div className="absolute top-[65%] -translate-y-1/2 left-1/2 -translate-x-1/2 w-2 h-2 bg-yellow-400 rounded-full" />
                  <span className="absolute top-[68%] left-1/2 -translate-x-1/2 text-[7px] font-mono text-yellow-600 whitespace-nowrap">
                    子帧 {currentSubFrame + 1}
                  </span>
                </div>
              )} */}
            </div>

            {/* Empty state guide */}
            {keyframeIntervals.length === 0 && !dragSelection && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-5">
                <p className="text-xs font-medium text-slate-400 bg-white/80 px-4 py-2 rounded-full backdrop-blur-sm shadow-sm">
                  拖拽选择帧区间后，点击「设为关键帧」进行编辑
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Status Bar */}
        <div className="h-7 bg-slate-800 text-slate-400 flex items-center px-4 gap-6 shrink-0 text-[10px] font-medium">
          <span>当前帧: <b className="text-white font-mono">{currentFrame + 1}</b></span>
          <span>区间: <b className="text-white">{keyframeIntervals.length}</b></span>
          <span>总帧: <b className="text-white font-mono">{simulationFrameCount}</b></span>
          <div className="flex-1" />
          {unsaved && <span className="flex items-center gap-1 text-amber-400"><div className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse" /> 有未保存的更改</span>}
        </div>
      </div>
      {/* ===== MODALS ===== */}
      {/* Save confirm */}
      <Modal isOpen={showSaveConfirm} onClose={() => setShowSaveConfirm(false)} title="保存编辑" width="max-w-sm">
        <p className="text-sm text-slate-600 mb-5">你有未保存的更改，离开前是否保存？</p>
        <div className="flex gap-2">
          <button onClick={() => navigate(-1)} className="flex-1 px-4 py-2.5 text-sm text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors font-medium">不保存</button>
          <button onClick={doSave} className="flex-1 px-4 py-2.5 text-sm text-white bg-brand rounded-lg hover:bg-brand-dark transition-colors font-bold shadow-sm">保存</button>
        </div>
      </Modal>

      {/* 插值选项弹窗（整合版） */}
      <Modal isOpen={showInterpolateModal} onClose={() => setShowInterpolateModal(false)} title="确认插帧" width="max-w-lg">
        <div className="space-y-4">
          <div className="p-3 bg-brand-50 rounded-lg border border-brand-100">
            <p className="text-xs text-brand font-medium leading-relaxed">
              插帧将根据您设定的关键帧（帧 {keyframes.map(k => k.frame).sort((a, b) => a - b).join(', ')}）自动计算中间帧的关节角度。
              <br />
              <span className="font-bold">注意：插帧后，该区间内原有的关节参数将被覆盖。</span>
            </p>
          </div>

          {/* 基座插值选项 */}
          <div className="space-y-2">
            <p className="text-sm font-bold text-slate-800">基座插值选项：</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { key: 'interpBasePos', label: '基座位置 (XY)' },
                { key: 'interpBaseHeight', label: '基座高度 (Z)' },
                { key: 'interpBaseQuat', label: '基座朝向 (四元数)' },
              ].map((opt) => (
                <label key={opt.key} className="flex items-center gap-2 p-2 rounded-lg border border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    checked={pythonInterpOptions[opt.key as keyof typeof pythonInterpOptions]}
                    onChange={(e) => {
                      setPythonInterpOptions(prev => ({ ...prev, [opt.key]: e.target.checked }));
                    }}
                    className="w-4 h-4 rounded text-brand focus:ring-brand"
                  />
                  <span className="text-xs font-medium text-slate-700">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* 关节选项 */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold text-slate-800">关节插值选项：</p>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={pythonInterpOptions.interpJoints}
                  onChange={(e) => {
                    setPythonInterpOptions(prev => ({ ...prev, interpJoints: e.target.checked }));
                  }}
                  className="w-4 h-4 rounded text-brand focus:ring-brand"
                />
                <span className="text-xs font-medium text-slate-700">启用关节插值</span>
              </label>
            </div>

            {pythonInterpOptions.interpJoints && (
              <div className="grid grid-cols-2 gap-2 max-h-[240px] overflow-y-auto p-1">
                {activeJointDefs.map(def => (
                  <label key={def.key} className="flex items-center gap-2 p-2 rounded-lg border border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedJointsForInterpolation.includes(def.key)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedJointsForInterpolation(prev => [...prev, def.key]);
                        } else {
                          setSelectedJointsForInterpolation(prev => prev.filter(k => k !== def.key));
                        }
                      }}
                      className="w-4 h-4 rounded text-brand focus:ring-brand"
                    />
                    <span className="text-xs font-medium text-slate-700">{def.label}</span>
                  </label>
                ))}
              </div>
            )}

            {pythonInterpOptions.interpJoints && (
              <div className="flex items-center justify-between pt-1">
                <button
                  onClick={() => setSelectedJointsForInterpolation(activeJointDefs.map(d => d.key))}
                  className="text-[10px] text-brand hover:underline"
                >
                  全选关节
                </button>
                <button
                  onClick={() => setSelectedJointsForInterpolation([])}
                  className="text-[10px] text-slate-400 hover:underline"
                >
                  全不选
                </button>
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <button onClick={() => setShowInterpolateModal(false)} className="flex-1 px-4 py-2.5 text-sm text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors font-medium">取消</button>
            <button
              onClick={doPythonInterpolation}
              disabled={isPythonBusy}
              className="flex-1 px-4 py-2.5 text-sm text-white bg-brand rounded-lg hover:bg-brand-dark transition-colors font-bold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isInterpolating ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  插值中...
                </>
              ) : (
                '确认插帧'
              )}
            </button>
          </div>
          <p className="text-[10px] text-amber-500 text-center">注意：插帧将覆盖当前区间内选中关节的已有参数</p>
        </div>
      </Modal>

      {/* ZMP风险检测结果弹窗 */}
      <Modal isOpen={showZmpModal} onClose={() => setShowZmpModal(false)} title="ZMP风险检测结果" width="max-w-lg">
        {zmpReport ? (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            {/* 评分 */}
            <div className={`p-4 rounded-lg border ${
              zmpReport.score >= 80 ? 'bg-green-50 border-green-200' :
              zmpReport.score >= 50 ? 'bg-amber-50 border-amber-200' :
              'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-slate-700">稳定性评分</span>
                <span className={`text-2xl font-black ${
                  zmpReport.score >= 80 ? 'text-green-600' :
                  zmpReport.score >= 50 ? 'text-amber-600' :
                  'text-red-600'
                }`}>{zmpReport.score}<span className="text-sm font-medium text-slate-400">/100</span></span>
              </div>
              <div className="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    zmpReport.score >= 80 ? 'bg-green-500' :
                    zmpReport.score >= 50 ? 'bg-amber-500' :
                    'bg-red-500'
                  }`}
                  style={{ width: `${zmpReport.score}%` }}
                />
              </div>
            </div>

            {/* 统计 */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                <p className="text-xs text-red-600 font-bold">高风险帧</p>
                <p className="text-lg font-black text-red-700">{zmpReport.high_risk.length} <span className="text-xs font-normal">帧</span></p>
                {zmpReport.high_risk.length > 0 && (
                  <p className="text-[10px] text-red-500 mt-1 font-mono">{zmpReport.high_risk.map(f => f + 1).join(', ')}</p>
                )}
              </div>
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                <p className="text-xs text-amber-600 font-bold">警告帧</p>
                <p className="text-lg font-black text-amber-700">{zmpReport.warning_risk.length} <span className="text-xs font-normal">帧</span></p>
                {zmpReport.warning_risk.length > 0 && (
                  <p className="text-[10px] text-amber-500 mt-1 font-mono">{zmpReport.warning_risk.map(f => f + 1).join(', ')}</p>
                )}
              </div>
            </div>

            {/* 最危险帧 */}
            {zmpReport.worst_text && (
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-xs font-bold text-slate-700 mb-1">最危险帧</p>
                <p className="text-sm text-slate-600">{zmpReport.worst_text}</p>
              </div>
            )}

            {/* 按方向统计 */}
            {Object.entries(zmpReport.by_direction).some(([, frames]) => Array.isArray(frames) && frames.length > 0) && (
              <div className="space-y-2">
                <p className="text-sm font-bold text-slate-700">风险方向分布</p>
                {Object.entries(zmpReport.by_direction)
                  .filter(([, frames]) => Array.isArray(frames) && frames.length > 0)
                  .map(([direction, frames]) => (
                    <div key={direction} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                      <span className="text-xs text-slate-600">{direction}</span>
                      <span className="text-xs font-mono text-slate-500">{frames.length} 帧: {frames.map(f => f + 1).join(', ')}</span>
                    </div>
                  ))}
              </div>
            )}

            {/* 详情列表 */}
            {zmpReport.details.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-bold text-slate-700">风险详情 ({zmpReport.details.length} 条)</p>
                <div className="space-y-1 max-h-[200px] overflow-y-auto">
                  {zmpReport.details.map((d, i) => (
                    <div key={i} className={`p-2 rounded-lg text-xs ${
                      d.level === 'high' ? 'bg-red-50 border border-red-100' : 'bg-amber-50 border border-amber-100'
                    }`}>
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${d.level === 'high' ? 'bg-red-500' : 'bg-amber-500'}`} />
                        <span className="font-bold">帧 {d.frame_idx + 1}</span>
                        <span className="text-slate-500">{d.direction}</span>
                        <span className={`ml-auto font-mono ${d.level === 'high' ? 'text-red-600' : 'text-amber-600'}`}>
                          ZMP{d.zmp_margin.toFixed(3)}m COM{d.com_margin.toFixed(3)}m
                        </span>
                      </div>
                      {d.reasons.length > 0 && (
                        <p className="text-[10px] text-slate-500 mt-1 pl-4">{d.reasons.join('；')}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 摘要 */}
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 whitespace-pre-wrap text-xs text-slate-600 font-mono leading-relaxed">
              {zmpReport.summary}
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-500 text-center py-8">暂无检测结果</p>
        )}
        <div className="flex gap-2 pt-4">
          <button onClick={() => setShowZmpModal(false)} className="flex-1 px-4 py-2.5 text-sm text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors font-medium">关闭</button>
        </div>
      </Modal>

      {/* ===== 视频导出遮罩层 ===== */}
      {isExportingVideo && (
        <div className="absolute inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl px-10 py-8 flex flex-col items-center gap-4 max-w-sm mx-4">
            <div className="relative">
              <RefreshCw size={40} className="animate-spin text-brand" />
              <Film size={20} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-brand/70" />
            </div>
            <div className="text-center">
              <p className="text-base font-bold text-slate-800">视频导出中，请稍候...</p>
              <p className="text-sm text-slate-400 mt-1">
                正在逐帧渲染 {displayFrames.length} 帧 · 30 FPS
              </p>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
              <div
                className="h-full bg-brand rounded-full transition-all duration-300"
                style={{ width: `${Math.round(((currentFrame + 1) / Math.max(1, displayFrames.length)) * 100)}%` }}
              />
            </div>
            <p className="text-xs text-slate-400">
              帧 {currentFrame + 1} / {displayFrames.length}（约 {(displayFrames.length / 30).toFixed(0)} 秒）
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
